
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Button,
  InputField,
  TextAreaField,
  CircularLoader,
  NoticeBox, Tooltip, SingleSelect, SingleSelectOption, Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  IconUser16
} from '@dhis2/ui';
import { useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from './../auth/AuthContext';
import { POSITION_ATTRIBUTES } from '../staff/api/constants';
import { DATA_ELEMENTS } from './api/appraisalConstants';
import { OBJECTIVE_APPRAISAL_DE, APPRAISAL_STATUS_CODES } from './api/appraisalConstants';
import {
  ChevronRight, TrendingUp, Star, CheckCircle, Clock,
  Lock, Users, MessageCircleMoreIcon, Eye, ThumbsUp, ThumbsDown,
  User2Icon,
  UserCircle
} from 'lucide-react';
import './AppraisalForm.css'; 
import { GiCancel, GiCheckMark, GiClosedDoors } from 'react-icons/gi';
import { TbPointerDown } from "react-icons/tb";
import { useRatingScale } from './hooks/useRatingScale';
import { createRatingComponents } from './components/RatingComponents';
import { useWeightConfig } from './hooks/useWeightConfig';
import { getFileStorageKey, loadFilesFromDataStore, FILE_STORAGE_NAMESPACE } from '../objectivesWeeklyReporting/utils/fileStorage';

import { useLeadershipCompetencies } from './hooks/useLeadershipCompetencies';
import { useFiscalYear } from '../../shared/FiscalYearContext';
import { useCurrentUserPosition } from './hooks/useCurrentUserPosition';

// Objective Data Elements (from your PerformanceList)
const OBJECTIVE_DATA_ELEMENTS = {
  NAME: 'EuN5ztFnXsQ',
  DESCRIPTION: 'nWqW5BrcgP1',
  START_DATE: 'eDcVy661j9e',
  COMPLETION_DATE: 'J0rjxrDKn1l',
  APPROVAL_STATUS: 'DzQorireRBf',
};

// ─── Progress Bar ────────────────────────────────────────────────────────────
const ProgressBar = ({ value = 0 }) => {
  const fillClass =
    value >= 80 ? 'apr-progress-fill--high'
      : value >= 50 ? 'apr-progress-fill--mid'
        : 'apr-progress-fill--low';
  return (
    <div className="apr-progress-wrap">
      <div className="apr-progress-track">
        <div
          className={`apr-progress-fill ${fillClass}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="apr-progress-pct">{value}%</span>
    </div>
  );
};

// ─── Status Chip ─────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    approved: { label: ' Approved', cls: 'apr-chip--approved', icon: <CheckCircle size={11} /> },
    pending: { label: ' Pending', cls: 'apr-chip--pending', icon: <Clock size={11} /> },
    draft: { label: 'Draft', cls: 'apr-chip--draft', icon: null },
    submitted: { label: ' Submitted', cls: 'apr-chip--submitted', icon: <Clock size={11} /> },
    reviewed: { label: 'nReviewed', cls: 'apr-chip--reviewed', icon: <Star size={11} /> },
    finalized: { label: ' Finalized', cls: 'apr-chip--finalized', icon: <CheckCircle size={11} /> },
  };
  const cfg = map[status] || map['pending'];
  return (
    <span className={`apr-chip ${cfg.cls}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const RATING_DEFINITIONS = {
  '5': {
    label: 'Outstanding',
    description: 'Consistently exceeds expectations and demonstrates exceptional performance. Sets new standards and serves as a role model.',
    color: '#28a745',
    shortDesc: 'Exceptional performance'
  },
  '4': {
    label: 'Exceeds Expectations',
    description: 'Often exceeds targets and requirements. Shows initiative and consistently delivers high-quality results.',
    color: '#20d429',
    shortDesc: 'Above standard'
  },
  '3': {
    label: 'Meets Expectations',
    description: 'Consistently meets all job requirements and objectives. Demonstrates solid, reliable performance.',
    color: '#ffc107',
    shortDesc: 'Meets standard'
  },
  '2': {
    label: 'Needs Improvement',
    description: 'Sometimes falls short of requirements. Requires guidance and development in certain areas.',
    color: '#fd7e14',
    shortDesc: 'Below standard'
  },
  '1': {
    label: 'Unsatisfactory',
    description: 'Does not meet minimum requirements. Significant improvement needed or performance improvement plan required.',
    color: '#dc3545',
    shortDesc: 'Fails to meet standard'
  },
  '0': {
    label: 'Not Rated',
    description: 'No rating provided yet.',
    color: '#6c757d',
    shortDesc: 'Not evaluated'
  }
};

// ─── Workflow Stepper ─────────────────────────────────────────────────────────
const STEPS = [
  { key: APPRAISAL_STATUS_CODES.DRAFT, label: 'Draft', num: 1 },
  { key: APPRAISAL_STATUS_CODES.SUBMITTED, label: 'Submitted', num: 2 },
  { key: APPRAISAL_STATUS_CODES.REVIEWED, label: 'Manager Reviewed', num: 3 },
  { key: APPRAISAL_STATUS_CODES.FINALIZED, label: 'Finalized', num: 4 },
];

const STATUS_HINTS = {
  [APPRAISAL_STATUS_CODES.DRAFT]: 'You can edit and save as draft or submit for manager review.',
  [APPRAISAL_STATUS_CODES.SUBMITTED]: 'Awaiting manager review. Manager will provide their rating and comments.',
  [APPRAISAL_STATUS_CODES.REVIEWED]: 'Manager has provided their rating. Please review and finalize.',
  [APPRAISAL_STATUS_CODES.FINALIZED]: 'Appraisal is complete and locked.',
};

const RatingCardSelector = ({ options, selected, onChange, disabled, ratingScale }) => {
  return (
    <div className="rating-card-selector">
      {options.map((opt) => {
        const isSelected = selected === opt.value;
        const scaleColor = ratingScale?.[opt.value]?.color;
        const scaleBg = ratingScale?.[opt.value]?.bgColor;

        // Build inline styles for the selected state using the rating scale colors
        const cardStyle = {
          borderColor: isSelected ? (scaleColor || '#006b9e') : '#d0d7de',
          backgroundColor: isSelected ? (scaleBg || '#e6f2ff') : '#ffffff',
          color: isSelected ? (scaleColor || '#006b9e') : '#1e293b',
        };

        let cardClass = 'rating-card';
        if (isSelected) cardClass += ' rating-card--selected';
        if (disabled) cardClass += ' rating-card--disabled';

        return (
          <button
            key={opt.value}
            type="button"
            className={cardClass}
            style={cardStyle} // inline styles override the CSS class for selected state
            onClick={() => onChange(opt.value)}
            disabled={disabled}
          >
            <span style={{ fontWeight: 700, marginRight: '4px' }}>{opt.value}</span>
            – {opt.label}
          </button>
        );
      })}
    </div>
  );
};

const WorkflowStepper = ({ currentStatus, isTopLevelUser }) => {
  // For top-level users, show a simplified workflow WITHOUT Auto-Approved
  if (isTopLevelUser) {
    return (
      <div className="apr-stepper" style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', padding: '0 20px' }}>
        <div key="self-rating" className="apr-step" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="apr-step-circle apr-step-circle--done" style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#28a745', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle size={16} />
          </div>
          <span className="apr-step-label apr-step-label--done" style={{ fontSize: '12px', marginTop: '6px', fontWeight: 600, color: '#28a745' }}>Self Rating</span>
        </div>
        <div key="manager-review" className="apr-step" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="apr-step-circle apr-step-circle--active" style={{ width: '32px', height: '32px', borderRadius: '50%', background: currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? '#28a745' : '#006b9e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? <CheckCircle size={16} /> : <span>2</span>}
          </div>
          <span className={`apr-step-label ${currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? 'apr-step-label--done' : 'apr-step-label--active'}`} style={{ fontSize: '12px', marginTop: '6px', fontWeight: 600, color: currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? '#28a745' : '#006b9e' }}>
            Manager Review
          </span>
        </div>
        <div key="finalized" className="apr-step" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className={`apr-step-circle ${currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? 'apr-step-circle--done' : ''}`} style={{ width: '32px', height: '32px', borderRadius: '50%', background: currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? '#28a745' : '#d0d7de', color: currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? 'white' : '#666', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? <CheckCircle size={16} /> : <span>3</span>}
          </div>
          <span className={`apr-step-label ${currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? 'apr-step-label--done' : ''}`} style={{ fontSize: '12px', marginTop: '6px', fontWeight: 600, color: currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? '#28a745' : '#666' }}>
            Finalized
          </span>
        </div>
        <div className="apr-step-connector apr-step-connector--active" style={{ width: 'calc(100% - 100px)', left: '50px', top: '16px', position: 'absolute', height: '3px', background: currentStatus === APPRAISAL_STATUS_CODES.FINALIZED ? '#28a745' : '#006b9e', borderRadius: '2px' }} />
      </div>
    );
  }

  const stepOrder = [
    APPRAISAL_STATUS_CODES.DRAFT,
    APPRAISAL_STATUS_CODES.SUBMITTED,
    APPRAISAL_STATUS_CODES.MANAGER_REVIEWED,
    APPRAISAL_STATUS_CODES.FINALIZED,
  ];

  const currentIdx = stepOrder.indexOf(currentStatus);

  return (
    <div className="apr-stepper" 
         style={{ position: 'relative',
         display: 'flex',
         justifyContent: 'space-between',
         padding: '0 20px',
         marginTop: '20px'
         }}>
      <div
        className="apr-step-connector"
        style={{
          width: 'calc(100% - 100px)',
          left: '50px',
          top: '16px',
          position: 'absolute',
          height: '3px',
          background: `linear-gradient(to right, var(--apr-success) ${(currentIdx / (STEPS.length - 1)) * 100}%, var(--apr-border-light) ${(currentIdx / (STEPS.length - 1)) * 100}%)`,
          borderRadius: '2px',
        }}
      />
      {STEPS.map((step, idx) => {
        const isDone = idx < currentIdx;
        const isActive = step.key === currentStatus;

        let circleClass = '';
        let labelClass = '';
        let circleStyle = {};
        let labelStyle = {};
        if (isDone) { 
          circleClass = 'apr-step-circle--done'; 
          labelClass = 'apr-step-label--done';
          circleStyle = { background: '#28a745', color: 'white' };
          labelStyle = { color: '#28a745' };
        } else if (isActive) { 
          circleClass = 'apr-step-circle--active'; 
          labelClass = 'apr-step-label--active';
          circleStyle = { background: '#006b9e', color: 'white', border: '2px solid #006b9e' };
          labelStyle = { color: '#006b9e', fontWeight: 700 };
        } else {
          circleStyle = { background: '#d0d7de', color: '#666' };
          labelStyle = { color: '#666' };
        }

        return (
          <div className="apr-step" key={step.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className={`apr-step-circle ${circleClass}`} style={{ width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', ...circleStyle }}>
              {isDone ? <CheckCircle size={16} /> : step.num}
            </div>
            <span className={`apr-step-label ${labelClass}`} style={{ fontSize: '12px', marginTop: '8px', fontWeight: 600, ...labelStyle }}>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Employee Response Summary (Read-only display) ─────────────────────────────
const EmployeeResponseSummary = ({
  responseStatus,
  responseRemarks,
  acceptedTaskRating,
  acceptedBehaviouralRating,
  managerRatingTask,
  managerRatingBehavioural
}) => {
  if (!responseStatus) return null;

  const isAccepted = responseStatus === 'accepted';

  return (
    <div className="apr-card" style={{ marginTop: '1rem', backgroundColor: '#f8f9fa' }}>
      <div className="apr-card-header">
        <div className="apr-card-header-left">
          <span className="apr-card-icon"><MessageCircleMoreIcon size={17} /></span>
          <h4 className="apr-card-title">Employee's Response to Manager's Rating</h4>
        </div>
      </div>

      <div style={{ padding: '1rem' }}>
        {/* Response Status */}
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '1rem',
          backgroundColor: isAccepted ? '#d4edda' : '#f8d7da',
          borderLeft: `4px solid ${isAccepted ? '#28a745' : '#dc3545'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isAccepted ? (
              <ThumbsUp size={20} color="#28a745" />
            ) : (
              <ThumbsDown size={20} color="#dc3545" />
            )}
            <strong style={{ color: isAccepted ? '#155724' : '#721c24' }}>
              Employee {isAccepted ? 'ACCEPTED' : 'DISPUTED'} the Manager's Rating
            </strong>
          </div>
        </div>

        {/* Comparison of Ratings */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            <strong style={{ color: '#1565c0' }}>Manager's Recommended Rating:</strong>
            <div style={{ marginTop: '8px' }}>
              <div>Business Objectives: <strong>{managerRatingTask || 'Not rated'}</strong></div>
              <div>Behavioural Criteria: <strong>{managerRatingBehavioural || 'Not rated'}</strong></div>
              <div>Total Score: <strong>{((safeParse(managerRatingTask) + safeParse(managerRatingBehavioural)) / 2).toFixed(2)}</strong></div>
            </div>
          </div>

          {isAccepted && (
            <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #28a745' }}>
              <strong style={{ color: '#28a745' }}>Employee's Accepted Rating:</strong>
              <div style={{ marginTop: '8px' }}>
                <div>Business Objectives: <strong>{acceptedTaskRating || 'Not rated'}</strong></div>
                <div>Behavioural Criteria: <strong>{acceptedBehaviouralRating || 'Not rated'}</strong></div>
                <div>Total Score: <strong>{((safeParse(acceptedTaskRating) + safeParse(acceptedBehaviouralRating)) / 2).toFixed(2)}</strong></div>
              </div>
            </div>
          )}
        </div>

        {/* Employee Remarks */}
        {responseRemarks && (
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <strong>Employee's Remarks:</strong>
            <p style={{ marginTop: '8px', marginBottom: 0, lineHeight: 1.5, fontStyle: 'italic' }}>
              "{responseRemarks}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};


const EmployeeResponseSection = ({
  onFinalize,
  disabled,
  managerRatingTask,
  managerRatingBehavioural,
  managerComments,
  initialRemarks = ''
}) => {
  const [remarks, setRemarks] = useState(initialRemarks);
  const [agrees, setAgrees] = useState(true);

  return (
    <div className="apr-section">
      <div className="apr-card">
        <div className="apr-card-header">
          <div className="apr-card-header-left">
            <span className="apr-card-icon"><MessageCircleMoreIcon size={20} /></span>
            <h4 className="apr-card-title">Manager's Evaluation Review</h4>
          </div>
        </div>
        <div className="apr-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)',
            padding: '1.25rem',
            borderRadius: '10px',
            marginBottom: '1.5rem',
            border: '1px solid #bbdefb'
          }}>
            <h5 style={{ margin: 0, color: '#1565c0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 800 }}>
              <Eye size={16} /> Manager's Recommended Rating
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#546e7a', fontWeight: 700, textTransform: 'uppercase' }}>Business Objectives</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{managerRatingTask || 'Not rated'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#546e7a', fontWeight: 700, textTransform: 'uppercase' }}>Behavioural Criteria</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{managerRatingBehavioural || 'Not rated'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#546e7a', fontWeight: 700, textTransform: 'uppercase' }}>Total Score</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1565c0' }}>{((safeParse(managerRatingTask) + safeParse(managerRatingBehavioural)) / 2).toFixed(2)}</div>
              </div>
            </div>
            {managerComments && (
              <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #bbdefb', fontStyle: 'italic', color: '#37474f' }}>
                "<strong>Manager Comments:</strong> {managerComments}"
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontWeight: 800, marginBottom: '1rem', display: 'block', fontSize: '1.1rem' }}>
              Do you agree with the manager's rating?
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="button"
                onClick={() => setAgrees(true)}
                className="apr-btn"
                style={{
                  flex: 1,
                  background: agrees ? 'var(--apr-success)' : '#fff',
                  color: agrees ? '#fff' : 'var(--apr-text)',
                  border: `2px solid ${agrees ? 'var(--apr-success)' : 'var(--apr-border)'}`,
                  padding: '1rem'
                }}
              >
                ✓ I Agree
              </button>
              <button
                type="button"
                onClick={() => setAgrees(false)}
                className="apr-btn"
                style={{
                  flex: 1,
                  background: !agrees ? 'var(--apr-danger)' : '#fff',
                  color: !agrees ? '#fff' : 'var(--apr-text)',
                  border: `2px solid ${!agrees ? 'var(--apr-danger)' : 'var(--apr-border)'}`,
                  padding: '1rem'
                }}
              >
                ✗ I Disagree
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <TextAreaField
              label={agrees ? "Employee Remarks" : "Reason for Disagreement"}
              value={remarks}
              onChange={({ value }) => setRemarks(value)}
              rows={4}
              placeholder={agrees ? 'Provide any final comments...' : 'Please explain why you disagree...'}
              inputWidth="100%"
              disabled={disabled}
              required
            />
          </div>

          <button
            className="apr-btn apr-btn--success"
            style={{ width: '100%', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '1rem' }}
            onClick={() => remarks.trim() && onFinalize(remarks, agrees)}
            disabled={!remarks.trim() || disabled}
          >
            <GiCheckMark size={20} /> Finalize Appraisal
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Task Detail Modal ─────────────────────────────────────────────────────────
const TaskDetailModal = ({ task, onClose, engine }) => {
  const [fileResources, setFileResources] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // In TaskDetailModal component, update the fetchFiles function:
  useEffect(() => {
    if (!task?.eventId || !task?.objectiveId) {
      setFileResources([]);
      return;
    }

    const fetchFiles = async () => {
      setLoadingFiles(true);
      try {
        // Use the SAME key patterns as FileAttachment
        const keyPatterns = [
          // Pattern 1: objectiveId + eventId + week
          getFileStorageKey(task.objectiveId, task.eventId, task.selectedWeek || ''),
          // Pattern 2: objectiveId + eventId (NO week)
          getFileStorageKey(task.objectiveId, task.eventId, ''),
          // Pattern 3: objectiveId + 'new' + week
          getFileStorageKey(task.objectiveId, 'new', task.selectedWeek || ''),
          // Pattern 4: objectiveId + 'new' (NO week)
          getFileStorageKey(task.objectiveId, 'new', ''),
          // Pattern 5: Just objectiveId
          getFileStorageKey(task.objectiveId, '', ''),
          // Pattern 6: Just eventId
          getFileStorageKey('', task.eventId, ''),
          // Pattern 7: Just 'new'
          getFileStorageKey('', 'new', ''),
        ];

        const uniquePatterns = [...new Set(keyPatterns.filter(k => k))];

        let foundFiles = null;
        let foundKey = null;

        for (const key of keyPatterns) {
          try {
            const files = await loadFilesFromDataStore(engine, key);
            if (files && files.length > 0) {
              foundFiles = files;
              foundKey = key;
              break;
            }
          } catch (e) {
            // 404 is expected
            if (e.status !== 404) {
              console.warn(`Failed to fetch attachments with key: ${key}`, e);
            }
          }
        }

        if (foundFiles && foundFiles.length > 0) {
          setFileResources(foundFiles);
          console.log(`📎 [Modal] Found ${foundFiles.length} files with key: ${foundKey}`);
        } else {
          console.log('🔍 [Modal] No files found for any key pattern');
          setFileResources([]);
        }
      } catch (error) {
        console.warn('Failed to fetch file attachments:', error);
        setFileResources([]);
      } finally {
        setLoadingFiles(false);
      }
    };

    fetchFiles();
  }, [task?.eventId, task?.objectiveId, task?.selectedWeek, engine]);

  if (!task) return null;

  return (
    <Modal large onClose={onClose}>
      <ModalTitle>📋 Personal Performance Note Detail</ModalTitle>
      <ModalContent>
        <div style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}>{task.taskName}</h5>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div><strong>Week Ending:</strong> {task.selectedWeek || '—'}</div>
            <div><strong>Progress:</strong> {task.progressPercent}%</div>
            <div><strong>Status:</strong> {task.status || '—'}</div>
            <div><strong>Linked KPI:</strong> {task.kpiName || '—'}</div>
          </div>

          {task.taskDescription && (
            <div style={{ marginBottom: '12px' }}>
              <strong>Description:</strong>
              <p style={{ margin: '4px 0 0 0', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {task.taskDescription}
              </p>
            </div>
          )}
          {task.achievements && (
            <div style={{ marginBottom: '12px' }}>
              <strong>🏆 Achievements:</strong>
              <p style={{ margin: '4px 0 0 0', padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                {task.achievements}
              </p>
            </div>
          )}
          {task.challenges && (
            <div style={{ marginBottom: '12px' }}>
              <strong>⚠️ Challenges:</strong>
              <p style={{ margin: '4px 0 0 0', padding: '8px', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
                {task.challenges}
              </p>
            </div>
          )}
          {task.nextSteps && (
            <div style={{ marginBottom: '12px' }}>
              <strong>📋 Next Steps:</strong>
              <p style={{ margin: '4px 0 0 0', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                {task.nextSteps}
              </p>
            </div>
          )}

          <div style={{ marginTop: '16px' }}>
            <strong>📎 Attachments</strong>
            {loadingFiles ? (
              <CircularLoader small />
            ) : fileResources.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {fileResources.map((file, idx) => {
                  // Determine file icon based on type
                  const getFileIcon = (type) => {
                    if (type?.startsWith('image/')) return '🖼️';
                    if (type === 'application/pdf') return '📄';
                    if (type?.includes('word')) return '📝';
                    if (type?.includes('excel')) return '📊';
                    return '📎';
                  };
                  const icon = getFileIcon(file.type);

                  // Format file size
                  const formatSize = (bytes) => {
                    if (bytes < 1024) return `${bytes} B`;
                    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                  };
                  const size = formatSize(file.size || 0);

                  // Upload info
                  const uploadedAt = file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : '';
                  const uploadedBy = file.uploadedBy?.name || file.uploadedBy?.displayName || 'Unknown';

                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #e8e8e8',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <span style={{ fontSize: '1.2rem' }}>{icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, color: '#333', fontSize: '0.85rem' }}>
                            {file.displayName || file.name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>
                            {size}{uploadedAt && ` • ${uploadedAt}`}{uploadedBy && ` • by ${uploadedBy}`}
                          </div>
                        </div>
                      </div>
                      <a
                        href={file.data || file.url || `/api/fileResources/${file.id}/data`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          border: '1px solid #ddd',
                          color: '#e3fcda',
                          fontSize: '0.75rem',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#17990b',
                        }}
                      >
                        <TbPointerDown size={14} /> Download
                      </a>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span style={{ color: '#999', fontStyle: 'italic', marginLeft: '8px' }}>No attachments</span>
            )}
          </div>
        </div>
      </ModalContent>
      <ModalActions>
        <button
          style={{
            backgroundColor: '#f12f0c',
            border: '1px solid #ddd',
            borderRadius: '12px',
            padding: '4px 8px',
            fontSize: '14px',
            cursor: 'pointer',
            color: '#fbe6e6',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
          onClick={onClose}>
          <GiCancel /> Close
        </button>
      </ModalActions>
    </Modal>
  );
};


// Helper function
const safeParse = (str) => {
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const fetchObjectiveWithDetails = async (objectiveId, engine) => {
  if (!objectiveId || !engine) {
    return { objective: null, kras: [], kpis: [] };
  }

  try {
    // Fetch the main objective event
    const response = await engine.query({
      objective: {
        resource: `events/${objectiveId}`,
        params: {
          fields: 'event,program,programStage,orgUnit,trackedEntityInstance,enrollment,eventDate,status,dataValues[dataElement,value]'
        }
      }
    });

    const objective = response.objective;
    if (!objective) {
      return { objective: null, kras: [], kpis: [] };
    }

    // Fetch KRAs (Key Result Areas) - events linked to this objective
    // Using the correct program stage for KRAs
    const krasResponse = await engine.query({
      kras: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: 'BJb3qTgbcTF',
          filter: [`plJovUlxfQ0:eq:${objectiveId}`],
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 50
        }
      }
    });

    // Fetch KPIs (Key Performance Indicators) - events linked to this objective
    const kpisResponse = await engine.query({
      kpis: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: 'ivyCLjNLFrd', // KPIs program stage
          filter: [`plJovUlxfQ0:eq:${objectiveId}`],
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 50
        }
      }
    });

    const kras = krasResponse.kras?.events || [];
    const kpis = kpisResponse.kpis?.events || [];

    console.log(`📊 Found ${kras.length} KRAs and ${kpis.length} KPIs for objective ${objectiveId}`);

    // Process KRAs - handle both possible data element IDs
    const processedKras = kras.map(event => {
      const dataValues = event.dataValues || [];
      // Try both possible data element IDs for KPA name
      let name = dataValues.find(dv => dv.dataElement === 'obapz6Gnyla')?.value || 
                 dataValues.find(dv => dv.dataElement === 'name')?.value || '';
      
      let description = dataValues.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || 
                       dataValues.find(dv => dv.dataElement === 'description')?.value || '';
      
      let weight = parseInt(dataValues.find(dv => dv.dataElement === 'Xl4VEFoIFIw')?.value) || 
                  parseInt(dataValues.find(dv => dv.dataElement === 'weight')?.value) || 0;

      return {
        id: event.event,
        name: name || 'Unnamed KPA',
        description: description || '',
        weight: weight || 0,
        dataValues: dataValues
      };
    });

    // Process KPIs
    const processedKpis = kpis.map(event => {
      const dataValues = event.dataValues || [];
      let name = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 
                 dataValues.find(dv => dv.dataElement === 'name')?.value || '';
      
      let description = dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || 
                       dataValues.find(dv => dv.dataElement === 'description')?.value || '';
      
      let target = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || 
                  dataValues.find(dv => dv.dataElement === 'target')?.value || '';

      return {
        id: event.event,
        name: name || 'Unnamed KPI',
        description: description || '',
        target: target || '',
        progress: 0,
        dataValues: dataValues
      };
    });

    return {
      objective: objective,
      kras: processedKras,
      kpis: processedKpis
    };
  } catch (error) {
    console.error('Error fetching objective details:', error);
    return { objective: null, kras: [], kpis: [] };
  }
};

const getProgressColor = (progress) => {
  if (progress >= 100) return '#28a745';
  if (progress >= 50) return '#ffc107';
  return '#dc3545';
};

// ─── Score Row ────────────────────────────────────────────────────────────────
const ScoreRow = ({ label, hint, value, onChange, weight = '50', disabled = false, score = '' }) => (
  <div className="apr-score-row">
    <div className="apr-score-cell-main">
      <div className="apr-score-label">{label}</div>
      {hint && <div className="apr-score-hint">{hint}</div>}
    </div>
    <div className="apr-score-cell-rating">
      <InputField
        disabled={disabled}
        type="number"
        value={String(value ?? '')}
        onChange={({ value }) => onChange(value)}
        min="0"
        max="5"
        step="1"
        inputWidth="100%"
      />
    </div>
    <div className="apr-score-cell-weight">{weight}%</div>
    <div className="apr-score-cell-result">{score}</div>
  </div>
);

// ─── Objective Row ────────────────────────────────────────────────────────────
const ObjectiveRow = (({
  obj,
  isExpanded,
  onToggle,
  savingObjectiveId,
  onRatingChange,
  onCommentsChange,
  disabled = false,
  tasks = [],
  onViewTask = null,
  engine = null,
  isManagerView = false,
  canEditManager = false,
  onManagerRatingChange = null,
  onManagerCommentsChange = null,
  businessRatingScale = {},
  isPrintMode = false,        
  isTopLevelUser = false, 
}) => {
  const selfRating =
    obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_RATING)?.value || '';
  const selfComments =
    obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS)?.value || '';

  const [localComments, setLocalComments] = useState(selfComments);
  const [localRating, setLocalRating] = useState(selfRating);
  const isTypingRef = useRef(false);
  const commentsTimeoutRef = useRef(null);
  
  // ✅ Track if we need to save on blur
  const pendingSaveRef = useRef(false);

  // Sync with prop changes when not typing
  useEffect(() => {
    if (!isTypingRef.current && selfComments !== localComments) {
      setLocalComments(selfComments);
    }
  }, [selfComments, localComments]);

  // Sync rating with prop changes
  useEffect(() => {
    if (selfRating !== localRating) {
      setLocalRating(selfRating);
    }
  }, [selfRating]);

  // ─── RATING CHANGE HANDLER - Update local state only ───
  const handleRatingChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      let numValue = value === '' ? '' : parseFloat(value);
      if (numValue !== '' && !isNaN(numValue)) {
        numValue = Math.max(0, Math.min(5, numValue));
        const clampedValue = parseFloat(numValue.toFixed(1));
        setLocalRating(String(clampedValue));
      } else {
        setLocalRating('');
      }
      // Mark that we need to save on blur
      pendingSaveRef.current = true;
    }
  };

  // ─── RATING BLUR HANDLER - Save when focus leaves ───
  const handleRatingBlur = () => {
    if (pendingSaveRef.current && localRating !== selfRating) {
      // Convert empty string to '' or keep the value
      const valueToSave = localRating || '';
      onRatingChange(valueToSave);
      pendingSaveRef.current = false;
    }
  };

  // ─── COMMENTS CHANGE HANDLER - Update local state only ───
  const handleCommentsChange = (value) => {
    isTypingRef.current = true;
    setLocalComments(value);
    // Mark that we need to save on blur
    pendingSaveRef.current = true;
    
    // Clear any existing timeout
    if (commentsTimeoutRef.current) {
      clearTimeout(commentsTimeoutRef.current);
    }
  };

  // ─── COMMENTS BLUR HANDLER - Save when focus leaves ───
  const handleCommentsBlur = () => {
    isTypingRef.current = false;
    if (pendingSaveRef.current && localComments !== selfComments) {
      onCommentsChange(localComments);
      pendingSaveRef.current = false;
    }
  };

  useEffect(() => {
    if (!isTypingRef.current && selfComments !== localComments) {
      setLocalComments(selfComments);
    }
  }, [selfComments, localComments]);

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (commentsTimeoutRef.current) {
        clearTimeout(commentsTimeoutRef.current);
      }
    };
  }, []);


  // ─── Task List with Attachments and View Details ────────────────────────────
  const TaskList = ({ tasks, onViewTask, engine }) => {
    const [attachmentCache, setAttachmentCache] = useState({});
    const [loadingAttachments, setLoadingAttachments] = useState({});
    const fetchedRef = useRef({});
    const isMounted = useRef(true);

    // Cleanup on unmount
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    // Fetch attachments for each task
    useEffect(() => {
      if (!tasks || tasks.length === 0 || !engine) return;

      const fetchAttachmentsForTask = async (task) => {
        if (!task?.eventId || !task?.objectiveId) {
          console.warn('⚠️ Task missing eventId or objectiveId:', task.taskName);
          return;
        }

        const cacheKey = `${task.objectiveId}_${task.eventId}`;

        // Skip if already fetched, currently loading, or component unmounted
        if (fetchedRef.current[cacheKey] || loadingAttachments[cacheKey] || !isMounted.current) return;

        // Mark as fetching
        if (isMounted.current) {
          setLoadingAttachments(prev => ({ ...prev, [cacheKey]: true }));
        }

        try {
          // Use the SAME key patterns as the FileAttachment component
          // The FileAttachment uses: getFileStorageKey(objectiveId, taskId, selectedWeek)
          // where taskId is the eventId
          const keyPatterns = [
            // Pattern 1: objectiveId + eventId + week (EXACT match from FileAttachment)
            getFileStorageKey(task.objectiveId, task.eventId, task.selectedWeek || ''),
            // Pattern 2: objectiveId + eventId (NO week)
            getFileStorageKey(task.objectiveId, task.eventId, ''),
            // Pattern 3: objectiveId + 'new' + week (for tasks created without eventId)
            getFileStorageKey(task.objectiveId, 'new', task.selectedWeek || ''),
            // Pattern 4: objectiveId + 'new' (NO week)
            getFileStorageKey(task.objectiveId, 'new', ''),
            // Pattern 5: Just objectiveId
            getFileStorageKey(task.objectiveId, '', ''),
            // Pattern 6: Just eventId
            getFileStorageKey('', task.eventId, ''),
          ];

          let foundFiles = null;
          let foundKey = null;

          for (const key of keyPatterns) {
            try {
              // Use the SAME loadFilesFromDataStore function
              const files = await loadFilesFromDataStore(engine, key);
              if (files && files.length > 0) {
                foundFiles = files;
                foundKey = key;
                console.log(`✅ Found ${files.length} attachments with key: ${key}`);
                break;
              }
            } catch (e) {
              // 404 is expected for patterns that don't exist
              if (e.status !== 404) {
                console.warn(`Failed to fetch attachments with key: ${key}`, e);
              }
            }
          }

          if (isMounted.current) {
            if (foundFiles && foundFiles.length > 0) {
              console.log(`📎 Found ${foundFiles.length} attachments for task ${task.taskName} using key: ${foundKey}`);
              setAttachmentCache(prev => ({ ...prev, [cacheKey]: foundFiles }));
            } else {
              console.log(`ℹ️ No attachments found for task: ${task.taskName}`);
            }
            // Mark as fetched regardless of result to prevent re-fetching
            fetchedRef.current[cacheKey] = true;
          }

        } catch (error) {
          console.warn('Failed to fetch attachments for task:', task.taskName, error);
          if (isMounted.current) {
            fetchedRef.current[cacheKey] = true;
          }
        } finally {
          if (isMounted.current) {
            setLoadingAttachments(prev => ({ ...prev, [cacheKey]: false }));
          }
        }
      };

      // Fetch for each task that hasn't been fetched yet
      tasks.forEach(task => {
        const cacheKey = `${task.objectiveId}_${task.eventId}`;
        if (!fetchedRef.current[cacheKey] && isMounted.current) {
          fetchAttachmentsForTask(task);
        }
      });

    }, [tasks, engine]);

    if (!tasks || tasks.length === 0) {
      return (
        <div style={{ color: '#999', fontStyle: 'italic', marginTop: '12px' }}>
          No PPNs recorded for this objective.
        </div>
      );
    }

    // Sort tasks by selectedWeek (most recent first)
    const sortedTasks = [...tasks].sort((a, b) =>
      new Date(b.selectedWeek) - new Date(a.selectedWeek)
    );

    // Helper to format file size
    const formatSize = (bytes) => {
      if (!bytes) return '0 B';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Helper to get file icon
    const getFileIcon = (type) => {
      if (type?.startsWith('image/')) return '🖼️';
      if (type === 'application/pdf') return '📄';
      if (type?.includes('word') || type?.includes('document')) return '📝';
      if (type?.includes('excel') || type?.includes('spreadsheet')) return '📊';
      if (type?.includes('powerpoint') || type?.includes('presentation')) return '📑';
      return '📎';
    };

    return (
      <div style={{ marginTop: '12px' }}>
        <div style={{ fontWeight: 600, marginBottom: '8px', color: '#2c3e50' }}>
          📋 Personal Performance Notes (PPN) ({tasks.length})
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {sortedTasks.map((task, idx) => {
            const progressColor = getProgressColor(task.progressPercent);
            const isComplete = task.progressPercent >= 100;
            const cacheKey = `${task.objectiveId}_${task.eventId}`;
            const attachments = attachmentCache[cacheKey] || [];
            const isLoading = loadingAttachments[cacheKey];

            return (
              <li
                key={idx}
                style={{
                  padding: '12px',
                  backgroundColor: isComplete ? '#e8f5e9' : '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  border: `1px solid ${isComplete ? '#c8e6c9' : '#e9ecef'}`,
                  transition: 'all 0.2s'
                }}
              >
                {/* Task Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {task.taskName}
                    {isComplete && <span style={{ marginLeft: '6px', color: '#28a745' }}>✅</span>}
                  </span>
                  <span style={{ fontSize: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    <span style={{ color: '#6c757d' }}>Week:</span>
                    <span style={{ fontWeight: 500 }}>{task.selectedWeek || '—'}</span>
                    <span style={{ color: '#6c757d' }}>Progress:</span>
                    <span style={{ fontWeight: 600, color: progressColor }}>
                      {task.progressPercent}%
                    </span>
                    <span style={{ color: '#6c757d' }}>Status:</span>
                    <span>{task.status || '—'}</span>
                  </span>
                </div>

                {/* Task Details - Achievements & Challenges */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  fontSize: '0.8rem',
                  marginBottom: '6px'
                }}>
                  {task.achievements && (
                    <span style={{ color: '#28a745' }}>🏆 {task.achievements.length > 80 ? task.achievements.substring(0, 80) + '...' : task.achievements}</span>
                  )}
                  {task.challenges && (
                    <span style={{ color: '#e65100' }}>⚠️ {task.challenges.length > 80 ? task.challenges.substring(0, 80) + '...' : task.challenges}</span>
                  )}
                </div>

                {/* Attachments Section */}
                <div style={{ marginTop: '8px' }}>
                  {isLoading ? (
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CircularLoader small /> Loading attachments...
                    </div>
                  ) : attachments.length > 0 ? (
                    <div>
                      <div style={{
                        fontSize: '0.7rem',
                        color: '#6c757d',
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        📎 Attachments ({attachments.length})
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {attachments.map((file, fileIdx) => (
                          <a
                            key={fileIdx}
                            href={file.data || file.url || `/api/fileResources/${file.id}/data`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 10px',
                              backgroundColor: '#e3f2fd',
                              borderRadius: '12px',
                              fontSize: '0.7rem',
                              color: '#1565c0',
                              textDecoration: 'none',
                              border: '1px solid #bbdefb',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bbdefb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                            title={file.displayName || file.name}
                          >
                            {getFileIcon(file.type)}
                            <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.displayName || file.name}
                            </span>
                            <span style={{ color: '#999', fontSize: '0.6rem' }}>
                              {formatSize(file.size)}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* View Details Button */}
                {onViewTask && (
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('🔍 View Details clicked for task:', task.taskName);
                        onViewTask(task);
                      }}
                      style={{
                        background: '#1976d2',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '6px 20px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        color: '#fff',
                        fontWeight: 500,
                        transition: 'background 0.2s, transform 0.1s',
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
                      onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      📋 View Full Details
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
  <div
    className={`apr-objective-card ${
      isExpanded ? 'apr-objective-card--expanded' : ''
    }`}
  >
    <div
  className="apr-obj-row"
  onClick={onToggle}
  style={{
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 150px 105px',
    alignItems: 'center',
    columnGap: '20px',
    width: '100%',
    cursor: 'pointer',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s',
  }}
>
  {/* Objective name and description - Column 1 */}
  <div
    className="apr-obj-name"
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      minWidth: 0,
      width: '100%',
    }}
  >
    <ChevronRight
      size={18}
      className={`apr-obj-expand-icon ${isExpanded ? 'apr-obj-expand-icon--open' : ''}`}
      style={{
        flexShrink: 0,
        marginTop: '2px',
        color: isExpanded ? 'var(--apr-primary)' : 'var(--apr-text-muted)',
      }}
    />

    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '5px',
        minWidth: 0,
        width: '100%',
      }}
    >
      <span
        style={{
          display: 'block',
          width: '100%',
          fontSize: '0.85rem',
          fontWeight: 700,
          lineHeight: 1.25,
          color: 'var(--apr-text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={obj.name}
      >
        {obj.name}
      </span>

      {obj.description && (
        <span
          style={{
            display: 'block',
            width: '100%',
            fontSize: '0.68rem',
            color: 'var(--apr-text-secondary)',
            fontWeight: 400,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={obj.description}
        >
          {obj.description.length > 180 ? `${obj.description.substring(0, 180)}...` : obj.description}
        </span>
      )}
    </div>
  </div>

  {/* KPA/KPI Progress Column - Column 2 */}
<div
  style={{
    width: '150px',
    minWidth: 0,
    justifySelf: 'center',
    textAlign: 'center',
  }}
>
  <ProgressBar value={obj.progress} />
  
  {/* Show KPA count */}
  {obj.kras && obj.kras.length > 0 && obj.kras.some(k => k.name && k.name.trim()) && (
    <div
      style={{
        marginTop: '2px',
        fontSize: '0.6rem',
        lineHeight: 1.2,
        color: 'var(--apr-text-muted)',
      }}
    >
      {obj.kras.filter(k => k.name && k.name.trim()).length} KRAs
      {obj.kras.some(k => k.weight > 0) && (
        <span>
          {' '}· Total: {obj.kras.reduce((sum, k) => sum + (k.weight || 0), 0)}%
        </span>
      )}
    </div>
  )}
  
  {/* Show KPI count */}
  {obj.kpis && obj.kpis.length > 0 && (
    <div
      style={{
        marginTop: '4px',
        fontSize: '0.65rem',
        lineHeight: 1.2,
        color: 'var(--apr-text-muted)',
      }}
    >
      {obj.kpis.filter(k => k.progress >= 100).length}/{obj.kpis.length} KPIs met
    </div>
  )}
</div>

  {/* Status Column - Column 3 */}
  <div
    style={{
      minWidth: '105px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
    }}
  >
    <StatusChip status={obj.approvalStatus || 'pending'} />
  </div>
</div>

    {/* Expandable panel */}
    <div
      className={`apr-obj-panel ${
        isExpanded || isPrintMode
          ? 'apr-obj-panel--visible'
          : 'apr-obj-panel--hidden'
      }`}
      style={{
        display: isExpanded || isPrintMode ? 'block' : 'none',
        maxHeight: 'none',
        overflow: 'visible',
      }}
    >
      <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            alignItems: 'stretch',
            width: '100%',
          }}
        >
        {/* LEFT COLUMN: Self Rating and Manager Rating */}
        <div style={{ minWidth: 0 }}>
          <h5 className="apr-obj-panel-title">
            <Star size={16} /> Rate This Objective
          </h5>

          <div className="apr-rating-row">
            <span className="apr-rating-label">
              Your Rating (0 – 5):
            </span>

            <div style={{ width: 120 }}>
              <InputField
                disabled={disabled}
                type="number"
                value={localRating}
                onChange={({ value }) => handleRatingChange(value)}
                onBlur={handleRatingBlur}
                min="0"
                max="5"
                step="1"
                inputWidth="100%"
              />
            </div>
          </div>

          <TextAreaField
            label="Your Comments (Describe your achievement...)"
            value={localComments}
            rows={6}
            inputWidth="100%"
            disabled={disabled}
            onChange={({ value }) => handleCommentsChange(value)}
            onBlur={handleCommentsBlur}
          />

          {savingObjectiveId === obj.id && (
            <div className="apr-saving-indicator">
              <CircularLoader small />
              <span>Auto-saving...</span>
            </div>
          )}

          {/* Manager fields */}
          {isManagerView && (
            <>
              <hr
                style={{
                  margin: '1rem 0',
                  border: '1px dashed #e0e0e0',
                }}
              />

              <h5
                className="apr-obj-panel-title"
                style={{ color: '#4f46e5' }}
              >
                👔 Manager’s Evaluation
              </h5>

              <div className="apr-rating-row">
                <span className="apr-rating-label">
                  Manager Rating (0 – 5):
                </span>

                <div style={{ width: 120 }}>
                  {canEditManager ? (
                    <SingleSelect
                      selected={obj.managerRating || ''}
                      onChange={({ selected }) =>
                        onManagerRatingChange(obj.id, selected)
                      }
                      placeholder="Select..."
                      dense
                    >
                      <SingleSelectOption
                        value=""
                        label="Select..."
                      />

                      {Object.entries(
                        businessRatingScale || RATING_DEFINITIONS
                      )
                        .sort(
                          ([a], [b]) => Number(b) - Number(a)
                        )
                        .map(([key, def]) => (
                          <SingleSelectOption
                            key={key}
                            value={key}
                            label={`${key} – ${def.label}`}
                          />
                        ))}
                    </SingleSelect>
                  ) : (
                    <div
                      style={{
                        padding: '8px',
                        backgroundColor: obj.managerRating
                          ? businessRatingScale[
                              obj.managerRating
                            ]?.bgColor || '#f1f5f9'
                          : '#f8f9fa',
                        borderRadius: '4px',
                        textAlign: 'center',
                        border: `1px solid ${
                          obj.managerRating
                            ? businessRatingScale[
                                obj.managerRating
                              ]?.color || '#cbd5e1'
                            : '#e2e8f0'
                        }`,
                        fontWeight: 700,
                        color: obj.managerRating
                          ? businessRatingScale[
                              obj.managerRating
                            ]?.color
                          : '#64748b',
                      }}
                    >
                      {obj.managerRating
                        ? `${obj.managerRating} – ${
                            businessRatingScale[
                              obj.managerRating
                            ]?.label
                          }`
                        : '—'}
                    </div>
                  )}
                </div>
              </div>

              <TextAreaField
                label="Manager Comments (optional)"
                value={obj.managerComments || ''}
                rows={2}
                inputWidth="100%"
                disabled={!canEditManager}
                onChange={({ value }) =>
                  onManagerCommentsChange(obj.id, value)
                }
                placeholder="Provide specific feedback on this objective..."
              />
            </>
          )}
        </div>

        {/* RIGHT COLUMN: KPIs, KRAs and PPN Tasks - SINGLE COLUMN */}
<div style={{ 
  minWidth: 0, 
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
}}>
  {/* ===== n/KPA SECTION ===== */}
  {obj.kras && obj.kras.length > 0 && obj.kras.some(k => k.name && k.name.trim()) && (
    <>
      <h5
        className="apr-obj-panel-title"
        style={{
          color: 'var(--apr-text-secondary)',
          marginTop: '8px',
          marginBottom: '8px'
        }}
      >
        🎯 Key Performance Areas (KPAs)
        <span
          style={{
            marginLeft: '8px',
            fontSize: '0.7rem',
            fontWeight: 'normal',
            color: '#6c757d',
          }}
        >
          ({obj.kras.filter(k => k.name && k.name.trim()).length} KRAs)
        </span>
      </h5>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          paddingRight: '8px',
          marginBottom: '8px'
        }}
      >
        {obj.kras
          .filter((kra) => kra.name && kra.name.trim())
          .map((kra, idx) => (
            <div
              key={idx}
              style={{
                padding: '10px 12px',
                backgroundColor: '#fff',
                borderRadius: '6px',
                border: '1px solid var(--apr-border-light)',
                boxShadow: 'var(--apr-shadow-sm)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#2c3e50',
                  }}
                >
                  {kra.name}
                </span>
                {kra.weight !== undefined && kra.weight > 0 && (
                  <span
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--apr-text-muted)',
                      backgroundColor: '#e8f0fe',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontWeight: 600,
                    }}
                  >
                    Weight: {kra.weight}%
                  </span>
                )}
              </div>
              {kra.description && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--apr-text-secondary)',
                    marginTop: '4px',
                    lineHeight: 1.4,
                  }}
                >
                  {kra.description}
                </div>
              )}
            </div>
          ))}
      </div>
    </>
  )}

  {/* ===== KPI Details Section ===== */}
  {obj.kpis && obj.kpis.length > 0 && (
    <>
      <h5
        className="apr-obj-panel-title"
        style={{
          color: 'var(--apr-text-secondary)',
          marginBottom: '8px'
        }}
      >
        📊 KPI Details
        {obj.tasks && obj.tasks.length > 0 && (
          <span
            style={{
              marginLeft: '8px',
              fontSize: '0.7rem',
              fontWeight: 'normal',
              color: '#6c757d',
            }}
          >
            ({obj.tasks.length} PPN{obj.tasks.length > 1 ? 's' : ''} linked)
          </span>
        )}
      </h5>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          paddingRight: '8px',
          marginBottom: '8px'
        }}
      >
        {obj.kpis
          .filter((kpi) => kpi.name)
          .map((kpi, idx) => (
            <div
              key={idx}
              style={{
                padding: '12px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid var(--apr-border-light)',
                boxShadow: 'var(--apr-shadow-sm)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  marginBottom: '6px',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  }}
                >
                  {kpi.name}
                </span>

                <span
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--apr-text-muted)',
                  }}
                >
                  Measure: {kpi.target}
                  {kpi.taskCount > 0 && (
                    <span
                      style={{
                        marginLeft: '8px',
                        color: '#1976d2',
                      }}
                    >
                      ({kpi.taskCount} PPN{kpi.taskCount > 1 ? 's' : ''})
                    </span>
                  )}
                </span>
              </div>

              {kpi.description && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--apr-text-secondary)',
                    marginTop: '4px',
                    fontStyle: 'italic',
                  }}
                >
                  {kpi.description}
                </div>
              )}

              <ProgressBar value={kpi.progress} />

              {kpi.taskCount > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                    marginTop: '4px',
                    fontSize: '0.7rem',
                    color: '#6c757d',
                  }}
                >
                  <span>
                    Based on {kpi.taskCount} PPN task{kpi.taskCount > 1 ? 's' : ''}
                  </span>
                  <span>Avg: {kpi.progress}%</span>
                </div>
              )}
            </div>
          ))}
      </div>
    </>
  )}

  {/* ===== PPN Tasks ===== */}
  <TaskList
    tasks={tasks}
    onViewTask={onViewTask}
    engine={engine}
  />

  {/* Per-objective manager review placeholder */}
  {isTopLevelUser && (
    <div
      className="obj-manual-review"
      style={{
        marginTop: '8px',
        padding: '16px',
        borderTop: '2px dashed #0f5132',
        backgroundColor: '#f5f8fa',
        borderRadius: '8px',
      }}
    >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <span style={{ fontSize: '16px' }}>✏️</span>

                <span
                  style={{
                    fontWeight: 700,
                    color: '#0f5132',
                    fontSize: '0.85rem',
                  }}
                >
                  Manager's Review 
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    '90px minmax(0, 1fr)',
                  gap: '12px',
                  marginBottom: '12px',
                }}
              >
                <div>
                  <span
                    style={{
                      display: 'block',
                      marginBottom: '3px',
                      fontSize: '0.7rem',
                      color: '#666',
                      fontWeight: 600,
                    }}
                  >
                    Manager Rating (0-5):
                  </span>

                  <div
                    style={{
                      minHeight: '28px',
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '2px 8px',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      borderBottom: '2px solid #0f5132',
                    }}
                  >
                    <span
                      style={{
                        color: '#999',
                        fontSize: '0.8rem',
                      }}
                    >
                      _____
                    </span>
                  </div>
                </div>

                <div>
                  <span
                    style={{
                      display: 'block',
                      marginBottom: '3px',
                      fontSize: '0.7rem',
                      color: '#666',
                      fontWeight: 600,
                    }}
                  >
                    Status:
                  </span>

                  <div
                    style={{
                      minHeight: '28px',
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '2px 8px',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      borderBottom: '2px solid #0f5132',
                    }}
                  >
                  </div>
                </div>
              </div>

              <div>
                <span
                  style={{
                    display: 'block',
                    marginBottom: '3px',
                    fontSize: '1rem', // Increased from 0.7rem
                    color: '#666',
                    fontWeight: 600,
                  }}
                >
                  Manager Comments: (Write your feedback here...)
                </span>

                <div
                  style={{
                    minHeight: '45px',
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    borderBottom: '2px solid #ccc',
                  }}
                >
                  <textarea
                      rows={6}
                      style={{
                        width: '100%',
                        minHeight: '90px',
                        boxSizing: 'border-box',
                        border: 'none',
                        borderBottom: '2px solid #ccc',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        padding: '8px',
                        fontSize: '0.7rem',
                        color: '#333',
                        resize: 'vertical',
                        outline: 'none',
                      }}
                    />
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);
});


// ─── Main Component ───────────────────────────────────────────────────────────
const AppraisalForm = ({
  positionTEI,
  programStage,
  orgUnit,
  readOnlyReport = false,
  isTopLevelUser = false,
  approvalHierarchy = { hasApprover: true },
  appraisalEvent = null,
  fiscalYearStart = '',
  fiscalYearEnd = '',
  selectedFiscalYear = null,
  availableFiscalYears = [],
  onDataLoaded = null,
  reportType = 'year-end',
}) => {

  const { weightConfig, loading: weightConfigLoading } = useWeightConfig(orgUnit);

  // Get weights with validation
  const businessWeight = weightConfig?.businessObjectives?.weight || 50;
  const leadershipWeight = weightConfig?.leadershipBehavioural?.weight || 50;
     

  // Validate total is 100 (if not, adjust temporarily for display)
  const validBusinessWeight = businessWeight + leadershipWeight === 100 ? businessWeight : 50;
  const validLeadershipWeight = businessWeight + leadershipWeight === 100 ? leadershipWeight : 50;

  const {
    competencies: leadershipCompetencies,
    loading: leadershipLoading
  } = useLeadershipCompetencies(orgUnit);

  // State for individual competency ratings
  const [competencyRatings, setCompetencyRatings] = useState({});
  const [competencyComments, setCompetencyComments] = useState({});
  const { fiscalYear } = useFiscalYear();
    
  // Update the calculateTotalScore function to use dynamic weights
  const calculateTotalScore = useCallback((taskRating, behaviouralRating) => {
    const task = safeParse(taskRating);
    const behavioural = safeParse(behaviouralRating);
    const businessPortion = (task * (validBusinessWeight / 100));
    const leadershipPortion = (behavioural * (validLeadershipWeight / 100));
    return (businessPortion + leadershipPortion).toFixed(2);
  }, [validBusinessWeight, validLeadershipWeight]);

  const normalizeTei = (input) => {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object' && input !== null) {
      return input.tei || input.trackedEntityInstance || input.id || input.value || '';
    }
    return String(input).trim();
  };            
  

  const positionTeiString = normalizeTei(positionTEI);

  const engine = useDataEngine();
  const { user } = useAuth();

  const { currentUserPosition, reportingToPosition, directReportPositions, loading: positionLoading } = useCurrentUserPosition(
    user?.id,
    user?.organisationUnits?.map(ou => ou.id) || []
  );

  const [appraisalData, setAppraisalData] = useState({
    employeeSelfRatingTask: '',
    employeeSelfRatingBehavioural: '',
    employeeSelfComments: '',
    managerRatingTask: '',
    managerRatingBehavioural: '',
    managerComments: '',
    managerBusinessComments: '',
    appraisalStatus: APPRAISAL_STATUS_CODES.DRAFT,
    eventId: null,
    employeeCompetencyRatings: '',
    employeeCompetencyComments: '',
    managerCompetencyRatings: '',
    managerCompetencyComments: '',
  });

  const [recentObjectives, setRecentObjectives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [savingObjectiveId, setSavingObjectiveId] = useState(null);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [roleCheckComplete, setRoleCheckComplete] = useState(false);
  const [incumbentTei, setIncumbentTei] = useState(null);
  const [managerCanView, setManagerCanView] = useState(false);

  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [fileResources, setFileResources] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [employeeResponseRemarks, setEmployeeResponseRemarks] = useState('');

  const [employeeResponseStatus, setEmployeeResponseStatus] = useState('');
  const [employeeAcceptedRatingTask, setEmployeeAcceptedRatingTask] = useState('');
  const [employeeAcceptedRatingBehavioural, setEmployeeAcceptedRatingBehavioural] = useState('');

  const [managerCompetencyRatings, setManagerCompetencyRatings] = useState({});
  const [managerCompetencyComments, setManagerCompetencyComments] = useState({});
  const [expandedCompId, setExpandedCompId] = useState(null);

  const [localComments, setLocalComments] = useState(appraisalData.employeeSelfComments);
  const [pendingSave, setPendingSave] = useState(false);
  const [localTaskRating, setLocalTaskRating] = useState(appraisalData.employeeSelfRatingTask);

  const autoSaveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);

  // Load business rating scale (for task/objectives)
  const { ratingScale: businessRatingScale, loading: businessScaleLoading } = useRatingScale(orgUnit, 'business');

  // Load leadership rating scale (for behavioural/leadership)
  const { ratingScale: leadershipRatingScale, loading: leadershipScaleLoading } = useRatingScale(orgUnit, 'leadership');

  // Create rating components with the loaded definitions
  const { EnhancedScoreRow: BusinessScoreRow, CriteriaSection } = useMemo(() =>
    createRatingComponents(businessRatingScale),
    [businessRatingScale]
  );

  const { EnhancedScoreRow: LeadershipScoreRow } = useMemo(() =>
    createRatingComponents(leadershipRatingScale),
    [leadershipRatingScale]
  );

  const isCurrentUserManager = useMemo(() => {
    return isManager && !isEmployee;
  }, [isManager, isEmployee]);

  const effectiveStart = selectedFiscalYear
    ? availableFiscalYears.find(y => y.year === selectedFiscalYear)?.start
    : fiscalYearStart;

  const effectiveEnd = selectedFiscalYear
    ? availableFiscalYears.find(y => y.year === selectedFiscalYear)?.end
    : fiscalYearEnd;

  const saveObjectiveRating = async (objectiveId, dataElement, value, silent = false) => {
    if (savingObjectiveId === objectiveId) {
      console.log('⏳ Save already in progress, skipping.');
      return;
    }

    setSavingObjectiveId(objectiveId);
    let retries = 0;
    const maxRetries = 2;

    while (retries <= maxRetries) {
      try {
        const response = await engine.query({
          event: {
            resource: `events/${objectiveId}`,
            params: {
              fields:
                'event,program,programStage,orgUnit,trackedEntityInstance,enrollment,eventDate,status,dataValues,lastUpdated',
            },
          },
        });

        const fullEvent = response.event;
        if (!fullEvent) throw new Error('Event not found');

        const updatedDataValues = [...(fullEvent.dataValues || [])];
        const idx = updatedDataValues.findIndex((dv) => dv.dataElement === dataElement);
        if (idx >= 0) {
          updatedDataValues[idx] = { dataElement, value };
        } else {
          updatedDataValues.push({ dataElement, value });
        }

        const payload = {
          event: fullEvent.event,
          program: fullEvent.program,
          programStage: fullEvent.programStage,
          orgUnit: fullEvent.orgUnit,
          trackedEntityInstance: fullEvent.trackedEntityInstance,
          eventDate: fullEvent.eventDate,
          status: fullEvent.status || 'ACTIVE',
          dataValues: updatedDataValues,
        };

        console.log('🔍 [saveObjectiveRating] Fetched event:', {
          eventId: objectiveId,
          enrollment: fullEvent.enrollment,
          trackedEntityInstance: fullEvent.trackedEntityInstance,
          program: fullEvent.program,
          programStage: fullEvent.programStage,
          orgUnit: fullEvent.orgUnit,
          dataValuesCount: fullEvent.dataValues?.length,
        });

        console.log('📤 [saveObjectiveRating] Payload to send:', {
          event: payload.event,
          enrollment: payload.enrollment,
          trackedEntityInstance: payload.trackedEntityInstance,
          dataValues: payload.dataValues.map(dv => `${dv.dataElement}=${dv.value}`).join(', '),
        });

        await engine.mutate({
          type: 'update',
          resource: `events/${objectiveId}`,
          data: payload,
          headers: { 'Content-Type': 'application/json' },
        });

        console.log('✅ Rating saved successfully');

        // Update local state
        setRecentObjectives((prev) =>
          prev.map((obj) =>
            obj.id === objectiveId ? { ...obj, dataValues: updatedDataValues } : obj,
          )
        );

        setSavingObjectiveId(null);
        return;
      } catch (err) {
        console.error(`❌ Save attempt ${retries + 1} failed:`, err);

        if (err.httpStatusCode !== 409 || retries >= maxRetries) {
          // Only show alert if not silent
          if (!silent) {
            alert(`Failed to save rating: ${err.message}`);
          } else {
            console.warn('Silent save failed:', err.message);
          }
          setSavingObjectiveId(null);
          throw err;
        }

        console.log(`🔄 Retrying (${retries + 1}/${maxRetries})...`);
        await new Promise((resolve) => setTimeout(resolve, 500));
        retries++;
      }
    }
  };

  useEffect(() => {
    if (currentUserPosition?.attributes) {
      const attrs = extractAttributes(currentUserPosition.attributes);
      console.log('🔍 Available attributes:', Object.keys(attrs));
      console.log('🔍 All attrs:', attrs);
      console.log('🔍 Position title candidates:', {
        'w3bwqUZoc6V': attrs['w3bwqUZoc6V'],
        'NWe61Sa41CS': attrs['NWe61Sa41CS'],
        'POSITION_ATTRIBUTES.POSITION_TITLE': attrs[POSITION_ATTRIBUTES.POSITION_TITLE]
      });
    }
  }, [currentUserPosition]);

  // Sync with prop changes
  useEffect(() => {
    if (appraisalData.employeeSelfComments !== localComments) {
      setLocalComments(appraisalData.employeeSelfComments);
    }
  }, [appraisalData.employeeSelfComments]);
  
  const handleCommentsChange = (value) => {
    setLocalComments(value);
    setPendingSave(true);
  };
  
  const handleCommentsBlur = () => {
    if (pendingSave && localComments !== appraisalData.employeeSelfComments) {
      setAppraisalData(prev => ({ ...prev, employeeSelfComments: localComments }));
      if (appraisalData.appraisalStatus !== APPRAISAL_STATUS_CODES.FINALIZED) {
        saveAppraisal(APPRAISAL_STATUS_CODES.DRAFT, true);
      }
      setPendingSave(false);
    }
  };

  // Sync with prop changes
  useEffect(() => {
    if (appraisalData.employeeSelfRatingTask !== localTaskRating) {
      setLocalTaskRating(appraisalData.employeeSelfRatingTask);
    }
  }, [appraisalData.employeeSelfRatingTask]);
  
  const handleRatingChange = (value) => {
    setLocalTaskRating(value);
    setPendingSave(true);
  };
  
  const handleRatingBlur = () => {
    if (pendingSave && localTaskRating !== appraisalData.employeeSelfRatingTask) {
      setAppraisalData(prev => ({ ...prev, employeeSelfRatingTask: localTaskRating }));
      if (appraisalData.appraisalStatus !== APPRAISAL_STATUS_CODES.FINALIZED) {
        saveAppraisal(APPRAISAL_STATUS_CODES.DRAFT, true);
      }
      setPendingSave(false);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 AppraisalData Debug:', {
        positionTeiString,
        eventId: appraisalData.eventId,
        status: appraisalData.appraisalStatus,
        employeeRatings: {
          task: appraisalData.employeeSelfRatingTask,
          behavioural: appraisalData.employeeSelfRatingBehavioural,
          comments: appraisalData.employeeSelfComments
        },
        managerRatings: {
          task: appraisalData.managerRatingTask,
          behavioural: appraisalData.managerRatingBehavioural,
          comments: appraisalData.managerComments
        }
      });
    }
  }, [positionTeiString, appraisalData]);

  // ── Auto‑calculate Business Overall Rating from objective ratings ──
  useEffect(() => {
    // Only calculate if we have objectives and the rating is not already set from loaded data
    if (recentObjectives.length === 0) return;
    
    // Check if we already have a rating from loaded data (not auto-calculated)
    const hasExistingRating = appraisalData.employeeSelfRatingTask && appraisalData.employeeSelfRatingTask !== '';
    
    // If we have an existing rating and the appraisal is finalized, don't overwrite
    if (hasExistingRating && appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED) {
      return;
    }
    
    const ratings = recentObjectives
      .map(obj => {
        const rating = obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_RATING)?.value;
        return rating ? parseFloat(rating) : null;
      })
      .filter(r => r !== null && !isNaN(r));

    let avg = '';
    if (ratings.length > 0) {
      const sum = ratings.reduce((a, b) => a + b, 0);
      avg = Math.round(sum / ratings.length).toString();
    }

    setAppraisalData(prev => ({
      ...prev,
      employeeSelfRatingTask: avg,
    }));
  }, [recentObjectives, appraisalData.employeeSelfRatingTask, appraisalData.appraisalStatus]);

  // Detect print mode and expand all objectives
    useEffect(() => {
      const handleBeforePrint = () => {
        console.log('🖨️ Before print - expanding all objectives');
        // First set print mode
        setIsPrintMode(true);
        // Then expand all objectives
        if (recentObjectives.length > 0) {
          setExpandedObjectiveId('ALL');
        }
        // Force re-render
        setRenderKey(prev => prev + 1);
        
        // ✅ ADD THIS: Force another re-render after a tiny delay
        setTimeout(() => {
          setRenderKey(prev => prev + 1);
        }, 50);
      };

      const handleAfterPrint = () => {
        console.log('🖨️ After print - collapsing all objectives');
        setIsPrintMode(false);
        setExpandedObjectiveId(null);
        setRenderKey(prev => prev + 1);
      };

      window.addEventListener('beforeprint', handleBeforePrint);
      window.addEventListener('afterprint', handleAfterPrint);

      return () => {
        window.removeEventListener('beforeprint', handleBeforePrint);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }, [recentObjectives]);

  // ── Auto‑calculate Manager Overall Business Rating ──
  useEffect(() => {
    const ratings = recentObjectives
      .map(obj => obj.managerRating ? parseFloat(obj.managerRating) : null)
      .filter(r => r !== null && !isNaN(r));

    let avg = '';
    if (ratings.length > 0) {
      const sum = ratings.reduce((a, b) => a + b, 0);
      avg = Math.round(sum / ratings.length).toString();
    }

    setAppraisalData(prev => ({
      ...prev,
      managerRatingTask: avg,
    }));
  }, [recentObjectives]);

  useEffect(() => {
    recentObjectivesRef.current = recentObjectives;
  }, [recentObjectives]);


  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      debounceTimeouts.current = {};
    };
  }, []);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Force expand when user initiates print
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        // User is about to print, expand everything immediately
        console.log('⌨️ Print shortcut detected - expanding objectives');
        setIsPrintMode(true);
        if (recentObjectives.length > 0) {
          setExpandedObjectiveId('ALL');
        }
        setRenderKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [recentObjectives]);

  const calculateObjectiveProgress = (tasks = []) => {
    if (!tasks || tasks.length === 0) return 0;
    const total = tasks.reduce((s, t) => s + (t.progressPercent || 0), 0);
    return Math.round(total / tasks.length);
  };

  const extractAttributes = (attributes = []) => {
    const map = {};
    attributes.forEach((attr) => (map[attr.attribute] = attr.value));
    return map;
  };

  const getIncumbentId = (position) => {
    if (!position || !position.attributes) return null;
    const attrs = extractAttributes(position.attributes);
    return attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || null;
  };

  const debounceTimeouts = useRef({});

  const debounceSave = useCallback((key, fn, delay = 500) => {
    if (debounceTimeouts.current[key]) {
      clearTimeout(debounceTimeouts.current[key]);
    }
    debounceTimeouts.current[key] = setTimeout(() => {
      delete debounceTimeouts.current[key];
      Promise.resolve()
        .then(() => fn())  // fn can be a function that already captures arguments
        .catch((err) => console.error('Debounced save failed:', err));
    }, delay);
  }, []);

  useEffect(() => {
    if (!positionTeiString || !user?.id) {
      setIsEmployee(false);
      setIsManager(false);
      setRoleCheckComplete(true);
      return;
    }

    let cancelled = false;

    const resolveRoles = async () => {
      try {
        const positionRes = await engine.query({
          position: {
            resource: `trackedEntityInstances/${positionTeiString}`,
            params: { fields: 'attributes[attribute,value]' }
          }
        });

        const posAttrs = extractAttributes(positionRes.position?.attributes || []);
        const incumbentIdentifier = posAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];

        if (!incumbentIdentifier) {
          console.warn('No incumbent identifier found for position');
          if (!cancelled) {
            setIsEmployee(false);
            setIsManager(false);
            setRoleCheckComplete(true);
          }
          return;
        }

        const isEmp = user?.id === incumbentIdentifier || user?.username === incumbentIdentifier;

        let isMgr = false;
        try {
          const posAttrs = extractAttributes(positionRes.position?.attributes || []);
          const managerPositionTei = posAttrs[POSITION_ATTRIBUTES.REPORTING_TO];

          if (managerPositionTei) {
            const mgrPosRes = await engine.query({
              mgrPosition: {
                resource: `trackedEntityInstances/${managerPositionTei}`,
                params: { fields: 'attributes[attribute,value]' }
              }
            });
            const mgrAttrs = extractAttributes(mgrPosRes.mgrPosition?.attributes || []);
            const mgrUserIdentifier = mgrAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
            isMgr = user?.id === mgrUserIdentifier || user?.username === mgrUserIdentifier;
          }
        } catch (err) {
          console.warn('Failed to check manager role:', err);
        }

        if (!cancelled) {
          setIsEmployee(isEmp);
          setIsManager(isMgr);
          setRoleCheckComplete(true);
          console.log('🔐 Roles resolved:', {
            userId: user?.id,
            incumbentIdentifier,
            isEmployee: isEmp,
            isManager: isMgr
          });
        }

      } catch (err) {
        console.error('Role resolution failed:', err);
        if (!cancelled) {
          setIsEmployee(user?.id === positionTeiString);
          setIsManager(false);
          setRoleCheckComplete(true);
        }
      }
    };

    resolveRoles();
    return () => { cancelled = true; };
  }, [positionTeiString, user?.id, user?.username, engine]);


  const isFinalizedAndLocked = appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED;
  const canEmployeeEdit = !readOnlyReport &&
    isEmployee && !isFinalizedAndLocked &&
    appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.DRAFT;

  const canManagerEdit = !readOnlyReport &&
    (appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED ||
      appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.REVIEWED) &&
    !employeeResponseStatus && // employee hasn't responded yet
    (isManager || managerCanView);

  const getSixMonthsAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };


  const recentObjectivesRef = useRef([]);

  useEffect(() => {
    recentObjectivesRef.current = recentObjectives;
  }, [recentObjectives]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      debounceTimeouts.current = {};
    };
  }, []);

  useEffect(() => {
  if (!positionTeiString || !programStage || !orgUnit) return;
  let cancelled = false;

  const loadAppraisal = async () => {
      try {
        if (appraisalEvent) {
          console.log('📋 Loading appraisal from parent event:', {
            eventId: appraisalEvent.eventId,
            rawStatus: appraisalEvent.appraisalStatus,
            employeeTask: appraisalEvent.employeeSelfRatingTask,
            employeeBehavioural: appraisalEvent.employeeSelfRatingBehavioural,
            employeeComments: appraisalEvent.employeeSelfComments,
            employeeCompetencyRatings: appraisalEvent.employeeCompetencyRatings,
          });

          if (!cancelled) {
            // ✅ PARSE competency ratings JSON - IMPROVED with multiple strategies
            let parsedCompetencyRatings = {};
            let parsedCompetencyComments = {};
            const employeeCompetencyRatingsRaw = appraisalEvent.employeeCompetencyRatings || '';
            const employeeCompetencyCommentsRaw = appraisalEvent.employeeCompetencyComments || '';

            // Try multiple parsing strategies for ratings
            try {
              if (employeeCompetencyRatingsRaw) {
                parsedCompetencyRatings = JSON.parse(employeeCompetencyRatingsRaw);
                console.log('✅ Parsed competency ratings from parent event:', parsedCompetencyRatings);
              }
            } catch (parseError) {
              console.warn('Failed to parse competency ratings as JSON:', parseError);
              try {
                const fixed = employeeCompetencyRatingsRaw.replace(/'/g, '"');
                parsedCompetencyRatings = JSON.parse(fixed);
                console.log('✅ Parsed competency ratings with fixed quotes:', parsedCompetencyRatings);
              } catch (e2) {
                console.warn('Failed to parse competency ratings with fixed quotes:', e2);
                parsedCompetencyRatings = {};
              }
            }

            // Try multiple parsing strategies for comments
            try {
              if (employeeCompetencyCommentsRaw) {
                parsedCompetencyComments = JSON.parse(employeeCompetencyCommentsRaw);
                console.log('✅ Parsed competency comments from parent event:', parsedCompetencyComments);
              }
            } catch (parseError) {
              console.warn('Failed to parse competency comments as JSON:', parseError);
              try {
                const fixed = employeeCompetencyCommentsRaw.replace(/'/g, '"');
                parsedCompetencyComments = JSON.parse(fixed);
                console.log('✅ Parsed competency comments with fixed quotes:', parsedCompetencyComments);
              } catch (e2) {
                console.warn('Failed to parse competency comments with fixed quotes:', e2);
                parsedCompetencyComments = {};
              }
            }

            setAppraisalData({
              employeeSelfRatingTask: appraisalEvent.employeeSelfRatingTask || '',
              employeeSelfRatingBehavioural: appraisalEvent.employeeSelfRatingBehavioural || '',
              employeeSelfComments: appraisalEvent.employeeSelfComments || '',
              managerRatingTask: appraisalEvent.managerRatingTask || '',
              managerRatingBehavioural: appraisalEvent.managerRatingBehavioural || '',
              managerComments: appraisalEvent.managerComments || '',
              managerBusinessComments: appraisalEvent.managerBusinessComments || '',
              appraisalStatus: appraisalEvent.appraisalStatus || APPRAISAL_STATUS_CODES.DRAFT,
              eventId: appraisalEvent.eventId,
              employeeCompetencyRatings: appraisalEvent.employeeCompetencyRatings || '',
              employeeCompetencyComments: appraisalEvent.employeeCompetencyComments || '',
              managerCompetencyRatings: appraisalEvent.managerCompetencyRatings || '',
              managerCompetencyComments: appraisalEvent.managerCompetencyComments || '',
            });

            // ✅ Set competency ratings state - CRITICAL: Use parsed values
            console.log('📊 Setting competency ratings from parsed data:', parsedCompetencyRatings);
            setCompetencyRatings(parsedCompetencyRatings);
            setCompetencyComments(parsedCompetencyComments);

            // ✅ Force a re-render to ensure competencies show ratings
            // This helps when competencies load after ratings are set
            setTimeout(() => {
              console.log('🔄 Refreshing competency ratings display...');
              setCompetencyRatings(prev => {
                console.log('📊 Current competency ratings state:', prev);
                return prev;
              });
            }, 100);

            // ✅ Load employee response data from parent event
            if (appraisalEvent.employeeResponseStatus) {
              setEmployeeResponseStatus(appraisalEvent.employeeResponseStatus);
              setEmployeeResponseRemarks(appraisalEvent.employeeResponseRemarks || '');
              setEmployeeAcceptedRatingTask(appraisalEvent.employeeAcceptedRatingTask || '');
              setEmployeeAcceptedRatingBehavioural(appraisalEvent.employeeAcceptedRatingBehavioural || '');
            }
          }
          return;
        }

      // Otherwise fetch from API
      const response = await engine.query({
        appraisal: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage,
            orgUnit,
            ouMode: 'ACCESSIBLE',
            trackedEntityInstance: positionTeiString,
            fields: 'event,dataValues[dataElement,value],status',
            pageSize: 1,
          },
        },
      });

      const event = response.appraisal?.events?.[0];
      if (event) {
        const dv = (de) => event.dataValues?.find(d => d.dataElement === de)?.value || '';
        const rawStatus = dv(DATA_ELEMENTS.APPRAISAL_STATUS);

        // ✅ PARSE competency ratings JSON from API - IMPROVED
        let parsedCompetencyRatings = {};
        let parsedCompetencyComments = {};
        const employeeCompetencyRatingsRaw = dv(DATA_ELEMENTS.EMPLOYEE_COMPETENCY_RATINGS) || '';
        const employeeCompetencyCommentsRaw = dv(DATA_ELEMENTS.EMPLOYEE_COMPETENCY_COMMENTS) || '';

        // Try multiple parsing strategies for ratings
        try {
          if (employeeCompetencyRatingsRaw) {
            parsedCompetencyRatings = JSON.parse(employeeCompetencyRatingsRaw);
            console.log('✅ Parsed competency ratings from API:', parsedCompetencyRatings);
          }
        } catch (parseError) {
          console.warn('Failed to parse competency ratings as JSON:', parseError);
          try {
            const fixed = employeeCompetencyRatingsRaw.replace(/'/g, '"');
            parsedCompetencyRatings = JSON.parse(fixed);
            console.log('✅ Parsed competency ratings with fixed quotes:', parsedCompetencyRatings);
          } catch (e2) {
            console.warn('Failed to parse competency ratings with fixed quotes:', e2);
            parsedCompetencyRatings = {};
          }
        }

        try {
          if (employeeCompetencyCommentsRaw) {
            parsedCompetencyComments = JSON.parse(employeeCompetencyCommentsRaw);
            console.log('✅ Parsed competency comments from API:', parsedCompetencyComments);
          }
        } catch (parseError) {
          console.warn('Failed to parse competency comments as JSON:', parseError);
          try {
            const fixed = employeeCompetencyCommentsRaw.replace(/'/g, '"');
            parsedCompetencyComments = JSON.parse(fixed);
            console.log('✅ Parsed competency comments with fixed quotes:', parsedCompetencyComments);
          } catch (e2) {
            console.warn('Failed to parse competency comments with fixed quotes:', e2);
            parsedCompetencyComments = {};
          }
        }

        console.log('📋 Loading appraisal from API:', {
          eventId: event.event,
          rawStatus,
          employeeTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK),
          employeeBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL),
          competencyRatings: parsedCompetencyRatings,
        });

        if (!cancelled) {
          setAppraisalData({
            employeeSelfRatingTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK) || '',
            employeeSelfRatingBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL) || '',
            employeeSelfComments: dv(DATA_ELEMENTS.EMPLOYEE_COMMENTS) || '',
            managerRatingTask: dv(DATA_ELEMENTS.MANAGER_RATING_TASK) || '',
            managerRatingBehavioural: dv(DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL) || '',
            managerComments: dv(DATA_ELEMENTS.MANAGER_COMMENTS) || '',
            managerBusinessComments: dv(DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS) || '',
            appraisalStatus: rawStatus ? rawStatus.toLowerCase() : APPRAISAL_STATUS_CODES.DRAFT,
            eventId: event.event,
            employeeCompetencyRatings: employeeCompetencyRatingsRaw,
            employeeCompetencyComments: employeeCompetencyCommentsRaw,
            managerCompetencyRatings: dv(DATA_ELEMENTS.MANAGER_COMPETENCY_RATINGS) || '',
            managerCompetencyComments: dv(DATA_ELEMENTS.MANAGER_COMPETENCY_COMMENTS) || '',
          });

          if (Object.keys(parsedCompetencyRatings).length > 0) {
            const validRatings = Object.values(parsedCompetencyRatings).filter(r => r !== '');
            if (validRatings.length > 0) {
              const avg = validRatings.reduce((sum, r) => sum + parseFloat(r), 0) / validRatings.length;
              // Use the loaded behavioural rating or calculate from competencies
              const loadedBehavioural = appraisalEvent.employeeSelfRatingBehavioural || avg.toFixed(1);
              setAppraisalData(prev => ({
                ...prev,
                employeeSelfRatingBehavioural: loadedBehavioural
              }));
            }
          }

          // ✅ Set competency ratings state
          setCompetencyRatings(parsedCompetencyRatings);
          setCompetencyComments(parsedCompetencyComments);

          // ✅ Load employee response data from API
          const responseStatus = dv(DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS);
          const responseRemarks = dv(DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS);
          const acceptedTask = dv(DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_TASK);
          const acceptedBehavioural = dv(DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_BEHAVIOURAL);

          if (responseStatus) {
            setEmployeeResponseStatus(responseStatus);
            setEmployeeResponseRemarks(responseRemarks || '');
            setEmployeeAcceptedRatingTask(acceptedTask || '');
            setEmployeeAcceptedRatingBehavioural(acceptedBehavioural || '');
          }
        }

        console.log('📦 managerRatingTask from API:', dv(DATA_ELEMENTS.MANAGER_RATING_TASK));

      } else {
        console.log('ℹ️ No existing appraisal found, starting fresh');
        if (!cancelled) {
          setAppraisalData({
            employeeSelfRatingTask: '',
            employeeSelfRatingBehavioural: '',
            employeeSelfComments: '',
            managerRatingTask: '',
            managerRatingBehavioural: '',
            managerComments: '',
            managerBusinessComments: '',
            appraisalStatus: APPRAISAL_STATUS_CODES.DRAFT,
            eventId: null,
          });
          setCompetencyRatings({});
          setCompetencyComments({});
        }
      }
    } catch (err) {
      console.error('Failed to load appraisal:', err);
    }
  };

  loadAppraisal();
  return () => { cancelled = true; };
}, [positionTeiString, programStage, orgUnit, engine, appraisalEvent]);

 useEffect(() => {
  // When competencyRatings changes, calculate the overall behavioural rating
  if (Object.keys(competencyRatings).length > 0) {
    const validRatings = Object.values(competencyRatings).filter(r => r !== '' && r !== null && r !== undefined);
    if (validRatings.length > 0) {
      const avg = validRatings.reduce((sum, r) => sum + parseFloat(r), 0) / validRatings.length;
      const avgStr = avg.toFixed(1);
      console.log(`📊 Calculated behavioural rating from ${validRatings.length} competencies: ${avgStr}`);
      
      // Only update if different from current
      setAppraisalData(prev => {
        if (prev.employeeSelfRatingBehavioural !== avgStr) {
          return { ...prev, employeeSelfRatingBehavioural: avgStr };
        }
        return prev;
      });
    }
  }
}, [competencyRatings]);

  useEffect(() => {
    if (!positionTeiString || !orgUnit) {
      console.warn('⏳ No position or orgUnit – skipping objectives load');
      return;
    }

    const start = effectiveStart;
    const end = effectiveEnd;

    if (!start || !end) {
      console.error('❌ Fiscal year dates (effectiveStart/end) are missing:', { start, end });
      setRecentObjectives([]);
      return;
    }

    console.log('🔍 Loading objectives for fiscal year:', { start, end });

    let cancelled = false;

    const loadRecentObjectives = async () => {
  try {
    console.log('📤 Sending objectives query...');

    const objectivesResponse = await engine.query({
      objectives: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: 'VIBtpvkpHFR',
          orgUnit: orgUnit,
          ouMode: 'ACCESSIBLE',
          fields: 'event,trackedEntityInstance,orgUnit,eventDate,dataValues[dataElement,value]',
          eventStartDate: start,
          eventEndDate: end,
          pageSize: 100,
        },
      },
    });

    if (cancelled) return;

    console.log('✅ Objectives query completed. Raw response:', objectivesResponse);

    const events = objectivesResponse.objectives?.events || [];
    console.log(`📊 Found ${events.length} raw objectives`);

    const allObjs = events.map((event) => ({
      id: event.event,
      trackedEntityInstance: event.trackedEntityInstance,
      name: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.NAME)?.value || 'Unnamed Objective',
      description: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.DESCRIPTION)?.value || '',
      startDate: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.START_DATE)?.value || '',
      completionDate: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.COMPLETION_DATE)?.value || '',
      approvalStatus: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.APPROVAL_STATUS)?.value || 'pending',
      positionTei: event.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value,
      enrollment: event.enrollment,
      orgUnit: event.orgUnit,
      eventDate: event.eventDate,
      dataValues: event.dataValues || [],
      managerRating: event.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.BUS_MANAGER_RATING)?.value || '',
      managerComments: event.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.BUS_MANAGER_COMMENTS)?.value || '',
      // Initialize empty arrays for KRAs and KPIs
      kras: [],
      kpis: [],
    }));

    console.log(`📦 ${allObjs.length} objectives before manual filter`);

    // ---- MANUAL FISCAL YEAR FILTER (string comparison) ----
    const startStr = start;
    const endStr = end;
    const filteredObjs = allObjs.filter(obj => {
      if (!obj.eventDate) return false;
      const dateStr = obj.eventDate.split('T')[0];
      return dateStr >= startStr && dateStr <= endStr;
    });

    console.log(`📊 ${allObjs.length} objectives before filter, ${filteredObjs.length} after`);

    if (filteredObjs.length === 0) {
      console.warn('⚠️ No objectives remain after fiscal year filter.');
      setRecentObjectives([]);
      return;
    }

    // --- Fetch KPIs (for progress) ---
    const kpisResponse = await engine.query({
      allKpis: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: 'ivyCLjNLFrd',
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 200,
        },
      },
    });

    const allKpis = kpisResponse.allKpis?.events || [];
    console.log(`📊 Found ${allKpis.length} total KPIs`);

    // Build KPI name map
    const kpiNameMap = {};
    allKpis.forEach(event => {
      const dataValues = event.dataValues || [];
      const kpiName = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value;
      if (event.event && kpiName) {
        kpiNameMap[event.event] = kpiName;
      }
    });

    // --- Fetch tasks (for progress) ---
    const tasksResponse = await engine.query({
      allTasks: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: 'Nv9nYNcBTdM',
          filter: [`SlgO60cMK3F:eq:${positionTeiString}`],
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 1000,
        },
      },
    });

    const allTasks = tasksResponse.allTasks?.events || [];
    console.log(`📋 Found ${allTasks.length} tasks for position ${positionTeiString}`);

    // ---- Build tasks by objective and task progress by KPI ----
    const tasksByObjective = {};
    const taskProgressByKpi = {};

    allTasks.forEach(event => {
      const dv = event.dataValues || [];
      const objectiveId = dv.find(d => d.dataElement === 'plJovUlxfQ0')?.value;
      const kpiId = dv.find(d => d.dataElement === 'uXeH0X0aaCl')?.value;
      const progress = parseInt(dv.find(d => d.dataElement === 'AElpNxzTdRh')?.value) || 0;
      const status = dv.find(d => d.dataElement === 'LuTwfuN34pc')?.value || '';

      // Build tasks by objective
      if (objectiveId) {
        if (!tasksByObjective[objectiveId]) {
          tasksByObjective[objectiveId] = [];
        }

        const taskObj = {
          eventId: event.event,
          taskName: dv.find(d => d.dataElement === 'I0QtZkMbzxN')?.value || 'Unnamed PPN',
          taskDescription: dv.find(d => d.dataElement === 'LR70eqTjB8m')?.value || '',
          progressPercent: progress,
          status: status,
          achievements: dv.find(d => d.dataElement === 'j9FdNkmPpXM')?.value || '',
          challenges: dv.find(d => d.dataElement === 'WSQ9nuXPnNL')?.value || '',
          nextSteps: dv.find(d => d.dataElement === 'M7PG2p2XsDA')?.value || '',
          selectedWeek: dv.find(d => d.dataElement === 'EiL3lPaL908')?.value || '',
          kpiId: kpiId || '',
          kpiName: kpiNameMap[kpiId] || '',
          objectiveId: objectiveId,
        };
        tasksByObjective[objectiveId].push(taskObj);
      }

      // Build task progress by KPI
      if (kpiId) {
        if (!taskProgressByKpi[kpiId]) {
          taskProgressByKpi[kpiId] = {
            totalProgress: 0,
            count: 0,
            tasks: [],
            statuses: []
          };
        }
        taskProgressByKpi[kpiId].totalProgress += progress;
        taskProgressByKpi[kpiId].count += 1;
        taskProgressByKpi[kpiId].tasks.push({
          progress,
          status,
          eventId: event.event
        });
        if (status) {
          taskProgressByKpi[kpiId].statuses.push(status);
        }
      }
    });

    // ---- Group KPIs by objective ----
    const kpisByObjective = {};

    allKpis.forEach(event => {
      const dataValues = event.dataValues || [];
      const objectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
      const kpiPositionTei = dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;

      // Check if this KPI belongs to any objective in filteredObjs
      const objective = filteredObjs.find(obj => obj.id === objectiveId);
      const isRelevant =
        kpiPositionTei === positionTeiString ||
        (objective && objective.positionTei === positionTeiString);

      if (objectiveId && isRelevant) {
        if (!kpisByObjective[objectiveId]) {
          kpisByObjective[objectiveId] = [];
        }

        const kpiName = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI';
        const kpiTarget = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || '1';
        const kpiAchieved = dataValues.find(dv => dv.dataElement === 'pAGDYpY5X6f')?.value || '0';

        // Get task progress for this KPI
        const taskData = taskProgressByKpi[event.event];
        let kpiProgress = 0;

        if (taskData && taskData.count > 0) {
          // Calculate average progress from tasks linked to this KPI
          kpiProgress = Math.round(taskData.totalProgress / taskData.count);
        } else {
          // Fallback: use the achieved value from KPI
          kpiProgress = Math.min(100, Math.round((parseFloat(kpiAchieved) / parseFloat(kpiTarget || 1)) * 100) || 0);
        }

        kpisByObjective[objectiveId].push({
          id: event.event,
          name: kpiName,
          target: kpiTarget,
          achieved: kpiProgress,
          progress: kpiProgress,
          status: dataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || '',
          completionDate: dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || '',
          taskCount: taskData?.count || 0,
          taskProgress: taskData?.totalProgress || 0
        });
      }
    });

    // Filter objectives linked to this position
    const objectivesForPosition = filteredObjs.filter(obj => {
      const directMatch = obj.trackedEntityInstance === positionTeiString;
      const dataElementMatch = obj.positionTei === positionTeiString;
      return directMatch || dataElementMatch;
    });

    console.log(`🎯 Objectives for position ${positionTeiString}: ${objectivesForPosition.length}`);

    // ===== FETCH KRAs AND KPIs FOR EACH OBJECTIVE =====
    const objectivesWithKrasAndKpis = await Promise.all(
      objectivesForPosition.map(async (obj) => {
        const objKpis = kpisByObjective[obj.id] || [];
        const kpiProgress = objKpis.length > 0
          ? objKpis.reduce((sum, k) => sum + k.progress, 0) / objKpis.length
          : 0;
        const completedKpis = objKpis.filter(k => k.progress >= 100).length;

        // Fetch KRAs and KPIs for this objective
        let kras = [];
        let kpis = [];
        
        try {
          if (obj.id && engine) {
            const details = await fetchObjectiveWithDetails(obj.id, engine);
            kras = details.kras || [];
            kpis = details.kpis || [];
            
            // If we have KPIs from the details, use those instead
            if (kpis.length > 0) {
              console.log(`✅ Found ${kpis.length} KPIs for objective ${obj.id}`);
            }
          }
        } catch (error) {
          console.warn(`⚠️ Failed to fetch KRAs/KPIs for objective ${obj.id}:`, error);
        }

        return {
          ...obj,
          kras: kras,
          kpis: kpis.length > 0 ? kpis : objKpis, // Use fetched KPIs if available, otherwise fallback
          tasks: tasksByObjective[obj.id] || [],
          progress: Math.round(kpiProgress),
          kpiCount: objKpis.length,
          completedKpis: completedKpis,
        };
      })
    );

    // Sort by eventDate (most recent first)
    objectivesWithKrasAndKpis.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

    if (!cancelled) {
      setRecentObjectives(objectivesWithKrasAndKpis);
      console.log(`✅ Set ${objectivesWithKrasAndKpis.length} objectives with KPI and task data`);
      objectivesWithKrasAndKpis.forEach(obj => {
        console.log(`   - "${obj.name}" → KRAs: ${obj.kras?.length || 0}, KPIs: ${obj.kpiCount || 0}, Progress: ${obj.progress}%, Tasks: ${obj.tasks?.length || 0}`);
      });
    }
  } catch (err) {
    console.error('❌ Error in loadRecentObjectives:', err);
    setRecentObjectives([]);
  }
};

    loadRecentObjectives();
    return () => { cancelled = true; };
  }, [positionTeiString, orgUnit, effectiveStart, effectiveEnd, engine]);

  useEffect(() => {
    if (onDataLoaded && !loading && !leadershipLoading && appraisalData) {
      // Prepare data for the report
      const reportData = {
        appraisalData: appraisalData,
        recentObjectives: recentObjectives,
        competencyRatings: competencyRatings,
        leadershipCompetencies: leadershipCompetencies,
        businessRatingScale: businessRatingScale,
        leadershipRatingScale: leadershipRatingScale,
        employeeName: user?.displayName || user?.username || 'Employee',
        positionTitle: currentUserPosition?.positionTitle || 'Position',
        orgUnitName: orgUnit?.name || 'Organization',
        fiscalYear: fiscalYear,
        isLoading: loading || leadershipLoading,
        managerCompetencyRatings: managerCompetencyRatings,
        managerCompetencyComments: managerCompetencyComments,
        isPrintMode: isPrintMode,
      };
      console.log('📊 [AppraisalForm] Sending report data to parent:', {
        hasAppraisalData: !!appraisalData,
        objectivesCount: recentObjectives?.length,
        competenciesCount: leadershipCompetencies?.length,
        status: appraisalData?.appraisalStatus,
      });
      onDataLoaded(reportData);
    }
  }, [
    onDataLoaded,
    loading,
    leadershipLoading,
    appraisalData,
    recentObjectives,
    competencyRatings,
    leadershipCompetencies,
    businessRatingScale,
    leadershipRatingScale,
    user,
    currentUserPosition,
    orgUnit,
    fiscalYear,
    managerCompetencyRatings,
    managerCompetencyComments,
  ]);

  // Robust print detection to ensure isPrintMode is set correctly
useEffect(() => {
  let printTimer;

  const handleBeforePrint = () => {
    console.log('🖨️ Print detected - hiding validation banner');
    setIsPrintMode(true);
    setRenderKey(prev => prev + 1);
    
    // Force a re-render after print starts
    clearTimeout(printTimer);
    printTimer = setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 100);
  };

  const handleAfterPrint = () => {
    console.log('🖨️ Print completed - showing validation banner');
    setIsPrintMode(false);
    setRenderKey(prev => prev + 1);
  };

  // Listen for both beforeprint and afterprint events
  if (window) {
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
  }

  return () => {
    if (window) {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    }
    clearTimeout(printTimer);
  };
}, []);

  const saveAppraisal = async (status, silent = false) => {
  // Prevent overlapping saves
  if (isSavingRef.current) {
    console.log('⏳ Save already in progress, skipping.');
    return;
  }

  isSavingRef.current = true;
  try {
    if (!silent) setLoading(true);

    // ✅ If submitting, validate required fields
    if (status === APPRAISAL_STATUS_CODES.SUBMITTED) {
      // Validate Self-Assessment Comments
      if (!appraisalData.employeeSelfComments || !appraisalData.employeeSelfComments.trim()) {
        alert('❌ Please provide Self-Assessment Comments before submitting.');
        isSavingRef.current = false;
        if (!silent) setLoading(false);
        return;
      }

      // Validate ALL competencies have ratings
      const hasAllCompetencyRatings = leadershipCompetencies.every(comp => {
        const rating = competencyRatings[comp.id];
        return rating && rating !== '';
      });

      if (!hasAllCompetencyRatings) {
        const missingCompetencies = leadershipCompetencies
          .filter(comp => !competencyRatings[comp.id] || competencyRatings[comp.id] === '')
          .map(comp => `- ${comp.name}`)
          .join('\n');
        
        alert(`❌ Please provide ratings for all Leadership Competencies before submitting.\n\nMissing ratings for:\n${missingCompetencies}`);
        isSavingRef.current = false;
        if (!silent) setLoading(false);
        return;
      }

      // Validate Overall Business Rating
      if (!appraisalData.employeeSelfRatingTask || appraisalData.employeeSelfRatingTask === '') {
        alert('❌ Please complete your Business Objectives ratings before submitting.');
        isSavingRef.current = false;
        if (!silent) setLoading(false);
        return;
      }
    }

    // Build dataValues
    const competencyRatingsJSON = JSON.stringify(competencyRatings);
    const competencyCommentsJSON = JSON.stringify(competencyComments);
    const managerCompetencyRatingsJSON = JSON.stringify(managerCompetencyRatings);
    const managerCompetencyCommentsJSON = JSON.stringify(managerCompetencyComments);

    const dataValues = [
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS, value: appraisalData.managerBusinessComments || '' },
      { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: status }, // ✅ CRITICAL: Save the status
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_RATINGS, value: competencyRatingsJSON || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_COMMENTS, value: competencyCommentsJSON || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMPETENCY_RATINGS, value: managerCompetencyRatingsJSON || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMPETENCY_COMMENTS, value: managerCompetencyCommentsJSON || '' },
    ];

    // ✅ ADD THIS: Log the data being saved
    console.log('💾 Saving appraisal with status:', status);
    console.log('📊 Data values being saved:', dataValues);

    const eventData = {
      program: 'yiT00XKrrHP',
      programStage,
      orgUnit,
      trackedEntityInstance: positionTeiString,
      eventDate: new Date().toISOString().split('T')[0],
      status: 'ACTIVE',
      dataValues,
    };

    // Create or update the event
    if (!appraisalData.eventId) {
      const checkResponse = await engine.query({
        check: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage,
            orgUnit,
            trackedEntityInstance: positionTeiString,
            fields: 'event',
            pageSize: 1,
          },
        },
      });
      const existingEvent = checkResponse.check?.events?.[0]?.event;
      if (existingEvent) {
        appraisalData.eventId = existingEvent;
      }
    }

    let result;
    if (appraisalData.eventId) {
      console.log(`🔄 Updating existing event: ${appraisalData.eventId}`);
      result = await engine.mutate({
        resource: `events/${appraisalData.eventId}`,
        type: 'update',
        data: eventData,
        headers: { 'Content-Type': 'application/json' },
      });
    } else {
      console.log('✨ Creating new event');
      result = await engine.mutate({
        resource: 'events',
        type: 'create',
        data: eventData,
        headers: { 'Content-Type': 'application/json' },
      });
      setAppraisalData(prev => ({ ...prev, eventId: result.response?.reference || prev.eventId }));
    }

    // ✅ CRITICAL: Update local status
    setAppraisalData(prev => ({ ...prev, appraisalStatus: status }));
    
    // ✅ Force a re-load of the appraisal data to confirm status was saved
    setTimeout(async () => {
      try {
        const refreshResponse = await engine.query({
          refresh: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage,
              orgUnit,
              trackedEntityInstance: positionTeiString,
              fields: 'event,dataValues[dataElement,value],status',
              pageSize: 1,
            },
          },
        });
        const refreshedEvent = refreshResponse.refresh?.events?.[0];
        if (refreshedEvent) {
          const refreshedStatus = refreshedEvent.dataValues?.find(d => d.dataElement === DATA_ELEMENTS.APPRAISAL_STATUS)?.value || '';
          console.log('🔄 Refreshed status from DB:', refreshedStatus);
        }
      } catch (e) {
        console.warn('Failed to refresh appraisal:', e);
      }
    }, 1000);

    // Show notification only if not silent
    if (!silent) {
      const statusLabel = status === APPRAISAL_STATUS_CODES.DRAFT ? 'saved as draft' : 'submitted';
      alert(`✅ Appraisal ${statusLabel} successfully!`);
    } else {
      console.log('✅ Appraisal auto‑saved (silent)');
    }
  } catch (err) {
    console.error('Save error:', err);
    if (!silent) {
      const errorMsg = err?.response?.body?.message || 'Failed to save appraisal';
      setError(`Save failed: ${errorMsg}`);
      alert(`❌ ${errorMsg}`);
    } else {
      console.warn('Silent save failed:', err.message);
    }
  } finally {
    isSavingRef.current = false;
    if (!silent) setLoading(false);
  }
};

  // ============================================================
  // NEW FUNCTIONS FOR TWO-WAY APPROVAL FLOW
  // ============================================================
  const getAuthHeader = () => {
    // Get the basic auth credentials from localStorage or context
    const basicAuth = localStorage.getItem('basicAuth');
    if (basicAuth) {
      return { 'Authorization': `Basic ${basicAuth}` };
    }
    // Fallback to getting from session
    const auth = sessionStorage.getItem('auth');
    if (auth) {
      try {
        const { username, password } = JSON.parse(auth);
        const encoded = btoa(`${username}:${password}`);
        return { 'Authorization': `Basic ${encoded}` };
      } catch (e) {
        console.warn('Failed to parse auth');
      }
    }
    return {};
  };

 // Helper function to save appraisal data using direct fetch
const saveAppraisalData = async (dataValues, status) => {
  console.log('💾 [saveAppraisalData] Saving with status:', status);
  console.log('📊 [saveAppraisalData] Data values:', dataValues);

  try {
    // ✅ Get the current event data first
    const fullEventResponse = await engine.query({
      event: {
        resource: `events/${appraisalData.eventId}`,
        params: { 
          fields: 'event,program,programStage,orgUnit,trackedEntityInstance,eventDate,status,enrollment,dataValues[dataElement,value]' 
        }
      }
    });

    const fullEvent = fullEventResponse.event;
    if (!fullEvent) throw new Error('Event not found');

    // ✅ Merge existing data values with new ones
    const existingDataValues = fullEvent.dataValues || [];
    const updatedDataValues = [...existingDataValues];

    dataValues.forEach(newDv => {
      const idx = updatedDataValues.findIndex(dv => dv.dataElement === newDv.dataElement);
      if (idx >= 0) {
        updatedDataValues[idx] = { dataElement: newDv.dataElement, value: newDv.value };
      } else {
        updatedDataValues.push({ dataElement: newDv.dataElement, value: newDv.value });
      }
    });

    // ✅ Create the payload
    const payload = {
      event: fullEvent.event,
      program: fullEvent.program,
      programStage: fullEvent.programStage,
      orgUnit: fullEvent.orgUnit,
      trackedEntityInstance: fullEvent.trackedEntityInstance,
      eventDate: fullEvent.eventDate,
      status: fullEvent.status || 'ACTIVE',
      dataValues: updatedDataValues,
    };

    console.log('📤 [saveAppraisalData] Updating event with payload:', {
      eventId: appraisalData.eventId,
      status: status,
      dataValuesCount: updatedDataValues.length,
    });

    // ✅ Update the event
    await engine.mutate({
      type: 'update',
      resource: `events/${appraisalData.eventId}`,
      data: payload,
      headers: { 'Content-Type': 'application/json' }
    });

    console.log('✅ [saveAppraisalData] Event updated successfully');

  } catch (error) {
    console.error('❌ [saveAppraisalData] Error:', error);
    throw error;
  }
};

// ============================================================
// SAVE COMPETENCY RATINGS - Fixed version
// ============================================================
const saveCompetencyRatings = useCallback(async () => {
  if (!appraisalData.eventId) {
    console.warn('⚠️ No eventId found, cannot save competency ratings');
    return;
  }
  
  try {
    const ratingsJSON = JSON.stringify(competencyRatings);
    const commentsJSON = JSON.stringify(competencyComments);
    
    console.log('💾 Saving competency ratings:', { ratingsJSON, commentsJSON });
    
    // Save to the appraisal event
    await saveAppraisalData([
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_RATINGS, value: ratingsJSON },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_COMMENTS, value: commentsJSON },
    ], appraisalData.appraisalStatus);
    
    console.log('✅ Competency ratings saved successfully');
  } catch (error) {
    console.error('❌ Failed to save competency ratings:', error);
  }
}, [competencyRatings, competencyComments, appraisalData.eventId, appraisalData.appraisalStatus]);


  // Handle employee finalizing the appraisal after manager review
  const handleFinalizeAfterReview = async (remarks, agrees) => {
    if (!remarks || !remarks.trim()) {
      alert('Please provide remarks before finalizing.');
      return;
    }

    try {
      setLoading(true);

      // Set the response data in state
      setEmployeeResponseStatus(agrees ? 'accepted' : 'disputed');
      setEmployeeResponseRemarks(remarks);
      setEmployeeAcceptedRatingTask(appraisalData.managerRatingTask || '');
      setEmployeeAcceptedRatingBehavioural(appraisalData.managerRatingBehavioural || '');

      const dataValues = [
        { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS, value: appraisalData.managerBusinessComments || '' },
        { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: APPRAISAL_STATUS_CODES.FINALIZED },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS, value: agrees ? 'accepted' : 'disputed' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS, value: remarks },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_TASK, value: appraisalData.managerRatingTask || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
      ];

      await saveAppraisalData(dataValues, APPRAISAL_STATUS_CODES.FINALIZED);
      alert('✓ Appraisal finalized successfully!');
    } catch (err) {
      console.error('Failed to finalize appraisal:', err);
      alert('Failed to finalize appraisal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

 // Handle manager updating to REVIEWED status (after providing rating)
const handleManagerReview = async () => {
  if (!appraisalData.managerRatingTask || !appraisalData.managerRatingBehavioural) {
    alert('Please provide both Task and Behavioural ratings before reviewing.');
    return;
  }

  if (!appraisalData.eventId) {
    alert('No appraisal event found. Please save draft first.');
    return;
  }

  try {
    setLoading(true);

    // ✅ Use APPRAISAL_STATUS_CODES.REVIEWED (not MANAGER_REVIEWED)
    const newStatus = APPRAISAL_STATUS_CODES.REVIEWED;

    // Include competency ratings in the save
    const managerCompetencyRatingsJSON = JSON.stringify(managerCompetencyRatings);
    const managerCompetencyCommentsJSON = JSON.stringify(managerCompetencyComments);

    const dataValues = [
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS, value: appraisalData.managerBusinessComments || '' },
      { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: newStatus },
      { dataElement: DATA_ELEMENTS.MANAGER_COMPETENCY_RATINGS, value: managerCompetencyRatingsJSON || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMPETENCY_COMMENTS, value: managerCompetencyCommentsJSON || '' },
    ];

    console.log('📤 [handleManagerReview] Saving with status:', newStatus);
    console.log('📤 [handleManagerReview] Data values:', dataValues);

    await saveAppraisalData(dataValues, newStatus);
    
    // ✅ Update local state with REVIEWED status
    setAppraisalData(prev => ({ 
      ...prev, 
      appraisalStatus: APPRAISAL_STATUS_CODES.REVIEWED 
    }));
    
    alert('✅ Review submitted successfully. The employee can now view your ratings.');
  } catch (err) {
    console.error('Failed to submit manager review:', err);
    alert(`Failed to submit review: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

  // Handle submit with auto-approval for top-level users
const handleSubmit = async () => {
  // ✅ Check if there are missing fields and show alert
  const missingFields = getMissingFields ? getMissingFields() : [];
  
  if (missingFields.length > 0) {
    alert(
      `❌ Cannot Submit Appraisal\n\n` +
      `Please complete the following required fields before submitting:\n\n` +
      missingFields.map(f => `• ${f}`).join('\n') +
      `\n\nPlease fill in all required fields and try again.`
    );
    return;
  }

  try {
    setLoading(true);

    // ✅ Log the current status before submitting
    console.log('📤 Submitting appraisal with current status:', appraisalData.appraisalStatus);

    // For top-level users, auto-approve and finalize
  if (isTopLevelUser) {
    // ✅ Include competency ratings when saving
    const competencyRatingsJSON = JSON.stringify(competencyRatings);
    const competencyCommentsJSON = JSON.stringify(competencyComments);
    
    const dataValues = [
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: 'Auto-approved - CEO/Top-Level Executive' },
      { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: APPRAISAL_STATUS_CODES.FINALIZED },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS, value: 'accepted' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS, value: 'Auto-approved (CEO/Top-Level)' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_RATINGS, value: competencyRatingsJSON || '{}' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_COMMENTS, value: competencyCommentsJSON || '{}' },
    ];

      const eventData = {
        program: 'yiT00XKrrHP',
        programStage,
        orgUnit,
        trackedEntityInstance: positionTeiString,
        eventDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        dataValues,
      };

      if (!appraisalData.eventId) {
        const checkResponse = await engine.query({
          check: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage,
              orgUnit,
              trackedEntityInstance: positionTeiString,
              fields: 'event',
              pageSize: 1
            }
          }
        });
        const existingEvent = checkResponse.check?.events?.[0]?.event;
        if (existingEvent) {
          appraisalData.eventId = existingEvent;
        }
      }

      if (appraisalData.eventId) {
        await engine.mutate({
          resource: `events/${appraisalData.eventId}`,
          type: 'update',
          data: eventData,
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        const result = await engine.mutate({
          resource: 'events',
          type: 'create',
          data: eventData,
          headers: { 'Content-Type': 'application/json' },
        });
        setAppraisalData(prev => ({ ...prev, eventId: result.response?.reference || prev.eventId }));
      }

      setAppraisalData(prev => ({ ...prev, appraisalStatus: APPRAISAL_STATUS_CODES.FINALIZED }));
      alert('✅ Appraisal submitted and auto-approved!');
    } else {
      // ✅ Normal user - submit for review with status SUBMITTED
      await saveAppraisal(APPRAISAL_STATUS_CODES.SUBMITTED);
    }
  } catch (err) {
    console.error('Submit error:', err);
    alert('❌ Failed to submit appraisal. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const computed = useMemo(() => ({
    employeeTotal: ((safeParse(appraisalData.employeeSelfRatingTask) + safeParse(appraisalData.employeeSelfRatingBehavioural)) / 2).toFixed(2),
    managerTotal: ((safeParse(appraisalData.managerRatingTask) + safeParse(appraisalData.managerRatingBehavioural)) / 2).toFixed(2),
  }), [appraisalData]);

  if (loading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Saving appraisal…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <NoticeBox error title="Error">{error}</NoticeBox>
      </div>
    );
  }

  const isMidYear = programStage === 'xbEyVHbXlGK';

  if (!roleCheckComplete) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Checking permissions…</span>
      </div>
    );
  }

  const getPositionTitle = (position) => {
    if (!position) return 'Not Assigned';
    
    if (position.attributes && Array.isArray(position.attributes)) {
      const attrs = extractAttributes(position.attributes);
      // Use the correct attribute ID - NWe61Sa41CS is the Position Title
      const title = attrs['NWe61Sa41CS'];
      if (title) return title;
    }
    
    return 'Not Assigned';
  };

  // Add this helper function before the return statement
  const getMissingFields = () => {
    const missing = [];
    
    if (!appraisalData.employeeSelfComments || !appraisalData.employeeSelfComments.trim()) {
      missing.push('Self-Assessment Comments');
    }
    
    const missingCompetencies = leadershipCompetencies.filter(
      comp => !competencyRatings[comp.id] || competencyRatings[comp.id] === ''
    );
    if (missingCompetencies.length > 0) {
      missing.push(`Leadership Competencies (${missingCompetencies.map(c => c.name).join(', ')})`);
    }
    
    if (!appraisalData.employeeSelfRatingTask || appraisalData.employeeSelfRatingTask === '') {
      missing.push('Business Objectives Ratings');
    }
    
    return missing;
  };

  return (
    <div className="apr-page">
  {/* ── Staff Information Header ─────────────────────────────────── */}
  <div className="apr-section">
    <div className="apr-staff-header" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      borderBottom: '3px solid #0f5132',
      borderRadius: '12px 12px 0 0',
      marginBottom: '0',
      flexWrap: 'wrap',
      gap: '16px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative accent */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(15,81,50,0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }} />
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '6px',
        flex: '1',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          
          <div>
            <span style={{ 
              fontSize: '1.25rem', 
              fontWeight: 700, 
              color: '#0a2a1a',
              letterSpacing: '-0.01em',
              display: 'block'
            }}>
              {(() => {
                if (!user) return 'Unknown Staff';
                if (user.displayName && user.displayName.trim() && user.displayName !== user.username) {
                  return user.displayName.trim();
                }
                if (user.name && user.name.trim() && user.name !== user.username) {
                  return user.name.trim();
                }
                if (user.firstName || user.lastName) {
                  const firstName = user.firstName || '';
                  const lastName = user.lastName || '';
                  const fullName = `${firstName} ${lastName}`.trim();
                  if (fullName) return fullName;
                }
                if (user.givenName || user.familyName) {
                  const givenName = user.givenName || '';
                  const familyName = user.familyName || '';
                  const fullName = `${givenName} ${familyName}`.trim();
                  if (fullName) return fullName;
                }
                const username = user.username || 'Unknown Staff';
                return username.charAt(0).toUpperCase() + username.slice(1);
              })()}
            </span>
            
            {/* Status badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 12px',
              backgroundColor: '#0f5132',
              color: '#fff',
              borderRadius: '20px',
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
              marginTop: '2px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#4ade80',
                display: 'inline-block',
                animation: 'pulse 2s infinite'
              }} />
              Active
            </div>
          </div>
        </div>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '20px', 
          fontSize: '0.875rem',
          color: '#4a5568',
          marginTop: '2px',
          paddingLeft: '4px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'white',
            padding: '4px 14px',
            borderRadius: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          }}>
            <span style={{ fontWeight: 600, color: '#1a3a5c' }}>Position:</span>
            <span style={{ color: '#2d3748' }}>{getPositionTitle(currentUserPosition)}</span>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'white',
            padding: '4px 14px',
            borderRadius: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          }}>
            <span style={{ fontWeight: 600, color: '#1a3a5c' }}>FY:</span>
            <span style={{ color: '#2d3748' }}>
              {fiscalYear?.start && fiscalYear?.end 
                ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
                : 'Not Set'}
            </span>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'white',
            padding: '4px 14px',
            borderRadius: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          }}>
            <span style={{ fontWeight: 600, color: '#1a3a5c' }}>Objectives:</span>
            <span style={{
              fontWeight: 700,
              color: '#0f5132',
              background: '#e8f5e9',
              padding: '0 8px',
              borderRadius: '12px'
            }}>
              {recentObjectives.length}
            </span>
          </div>
        </div>
      </div>

      {/* Right side - optional action buttons or status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          padding: '6px 14px',
          background: 'rgba(15,81,50,0.1)',
          borderRadius: '20px',
          fontSize: '0.75rem',
          color: '#0f5132',
          fontWeight: 600,
          border: '1px solid rgba(15,81,50,0.15)'
        }}>
          {reportType === 'mid-year' ? 'Mid-Year' : 'Year-End'}
        </div>
        <div style={{
          padding: '6px 14px',
          background: '#fff',
          borderRadius: '20px',
          fontSize: '0.75rem',
          color: '#4a5568',
          fontWeight: 500,
          border: '1px solid #e2e8f0'
        }}>
          {new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </div>
      </div>
      
        {/* Status Badge */}
        <div style={{
          padding: '6px 16px',
          borderRadius: '20px',
          backgroundColor: appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED ? '#28a745' :
                          appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.REVIEWED ? '#006b9e' :
                          appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED ? '#ffc107' :
                          '#6c757d',
          color: 'white',
          fontWeight: 600,
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          whiteSpace: 'nowrap'
        }}>
          <Clock size={14} />
          {appraisalData.appraisalStatus?.toUpperCase() || 'DRAFT'}
        </div>
      </div>
      {/* ── Workflow Stepper ─────────────────────────────────── */}
      {!isTopLevelUser && (
        <WorkflowStepper
          className="hide-in-print"
          currentStatus={appraisalData.appraisalStatus}
          isTopLevelUser={isTopLevelUser}
        />
      )}

      {/* Status Information Banner - Simple version */}
      {!isTopLevelUser && !isPrintMode && (
      <div className="hide-in-print" style={{
        marginBottom: '1.25rem',
        padding: '0.6rem 1rem',
        backgroundColor: 'var(--apr-neutral-light)',
        borderRadius: '8px',
        border: '1px solid var(--apr-border-light)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Clock size={16} color="var(--apr-text-secondary)" />
          <span style={{ fontSize: '0.85rem', color: 'var(--apr-text)', fontWeight: 600 }}>
            {appraisalData.appraisalStatus?.toUpperCase() || 'DRAFT'}
          </span>
        </div>
        <span style={{ height: '14px', width: '1px', backgroundColor: 'var(--apr-border)' }} />
        <span style={{ fontSize: '0.8rem', color: 'var(--apr-text-secondary)', fontWeight: 500 }}>
          {STATUS_HINTS[appraisalData.appraisalStatus] || ' Complete the required sections below.'}
        </span>
      </div>
      )}

      {/* ── Recent Objectives ────────────────────────────────── */}
      <div className="apr-card">
         <div key={renderKey} className="apr-card-body">
            {recentObjectives.length > 0 ? (
              <>
                {/* Column Headers */}                
                <div className="apr-obj-header" style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 150px 105px',
                  alignItems: 'center',
                  columnGap: '20px',

                  padding: '10px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #e0e0e0',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: '#495057',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div style={{ textAlign: 'left' }}>Business Objective</div>
                  <div style={{ textAlign: 'center' }}>KRAs / KPIs</div> {/* Changed from "KPIs" to "KRAs / KPIs" */}
                  <div style={{ textAlign: 'center' }}>Status</div>
                </div>

                {/* Objective Rows */}
                {recentObjectives.map((obj, index) => {
                  const handleRatingChange = (value) => {
                    if (value === '') {
                      setRecentObjectives(prev =>
                        prev.map(o =>
                          o.id === obj.id
                            ? {
                              ...o,
                              dataValues: [
                                ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_RATING) || []),
                                { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_RATING, value: '' }
                              ]
                            }
                            : o
                        )
                      );
                      return;
                    }


                    let numValue = parseFloat(value);
                    if (isNaN(numValue)) return;
                    numValue = Math.max(0, Math.min(5, numValue));
                    const clampedValue = parseFloat(numValue.toFixed(1));

                    setRecentObjectives(prev =>
                      prev.map(o =>
                        o.id === obj.id
                          ? {
                            ...o,
                            dataValues: [
                              ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_RATING) || []),
                              { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_RATING, value: String(clampedValue) }
                            ]
                          }
                          : o
                      )
                    );

                    debounceSave(
                      `${obj.id}-rating`,
                      () => saveObjectiveRating(obj.id, OBJECTIVE_APPRAISAL_DE.SELF_RATING, String(clampedValue))
                    );
                  };

                  const handleCommentsChange = (value) => {
                    // Update local state immediately
                    setRecentObjectives(prev =>
                      prev.map(o =>
                        o.id === obj.id
                          ? {
                            ...o,
                            dataValues: [
                              ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS) || []),
                              { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS, value },
                            ],
                          }
                          : o
                      )
                    );

                    // Debounced silent save
                    debounceSave(
                      `${obj.id}-comments`,
                      () => saveObjectiveRating(obj.id, OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS, value, true), // silent = true
                      500
                    );
                  };

                  return (
                    <ObjectiveRow
                      key={`${obj.id}-${renderKey}-${index}`} 
                      obj={obj}
                     isExpanded={expandedObjectiveId === obj.id || expandedObjectiveId === 'ALL'} 
                      onToggle={() => {
                        // Only allow toggling if not in print mode
                        if (!isPrintMode) {
                          setExpandedObjectiveId(expandedObjectiveId === obj.id ? null : obj.id);
                        }
                      }}
                      savingObjectiveId={savingObjectiveId}
                      onRatingChange={handleRatingChange}
                      onCommentsChange={handleCommentsChange}
                      disabled={!canEmployeeEdit}
                      tasks={obj.tasks || []}
                      onViewTask={(task) => {
                        setSelectedTask({ ...task, objectiveId: obj.id });
                        setIsTaskModalOpen(true);
                      }}
                      engine={engine}
                      isManagerView={isManager && !isEmployee}
                      canEditManager={canManagerEdit}
                      onManagerRatingChange={(objectiveId, value) => {
                        // Update state
                        setRecentObjectives(prev =>
                          prev.map(o =>
                            o.id === objectiveId
                              ? { ...o, managerRating: value }
                              : o
                          )
                        );
                        // Auto‑save to backend
                        debounceSave(
                          `${objectiveId}-manager-rating`,
                          () => saveObjectiveRating(objectiveId, OBJECTIVE_APPRAISAL_DE.BUS_MANAGER_RATING, value)
                        );
                      }}
                      onManagerCommentsChange={(objectiveId, value) => {
                        setRecentObjectives(prev =>
                          prev.map(o =>
                            o.id === objectiveId
                              ? { ...o, managerComments: value }
                              : o
                          )
                        );
                        debounceSave(
                          `${objectiveId}-manager-comments`,
                          () => saveObjectiveRating(objectiveId, OBJECTIVE_APPRAISAL_DE.BUS_MANAGER_COMMENTS, value)
                        );
                      }}
                      businessRatingScale={businessRatingScale}
                      isPrintMode={isPrintMode}           
                      isTopLevelUser={isTopLevelUser} 
                    />
                  );
                })}
              </>
            ) : (
              <div className="apr-no-objectives">
                
                <div className="apr-no-objectives-text">No objectives found for the last 6 months.</div>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Employee Self-Appraisal Section with enhanced UI */}
{(isEmployee || (isManager && appraisalData.appraisalStatus !== APPRAISAL_STATUS_CODES.DRAFT)) && (
  <div className="apr-section">
    <div className="apr-card">
      <div className="apr-card-header">
        <div className="apr-card-header-left">
          <span className="apr-card-icon"><Star size={20} /></span>
          <h4 className="apr-card-title">
            {isEmployee ? "Overall Self-Evaluation" : "Employee's Self-Evaluation Review"}
          </h4>
        </div>
        <StatusChip status={appraisalData.appraisalStatus} />
      </div>

      {/* Business Performance Objectives */}
      <div className="apr-subsection apr-subsection--business">
        <div className="apr-subsection-header">
          <h5>📊 Business Performance Objectives</h5>
          <span className="apr-subsection-weight">{validBusinessWeight}% Weight</span>
        </div>
        
        <CriteriaSection           
        >
          <div>
            <div className="apr-score-header-row">
              <div className="apr-score-cell-main">Evaluation Criteria</div>
              <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Rating</div>
              <div className="apr-score-cell-weight">Weight</div>
              <div className="apr-score-cell-result">Weighted Score</div>
            </div>

            {/* Employee's Self Rating */}
            {canEmployeeEdit ? (
              (() => {
                                
                return (
                  <div className="apr-score-row">
                    <div className="apr-score-cell-main">
                      <div className="apr-score-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0d47a1'}}>
                        <UserCircle size={20} /> Your Overall Business Rating
                      </div>
                      <div className="apr-score-hint">
                        Calculated from your objective ratings ({recentObjectives.filter(o => o.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_RATING)?.value).length} rated)
                      </div>
                    </div>
                    <div className="apr-score-cell-rating">
                      <InputField
                        type="number"
                        value={localTaskRating}
                        onChange={({ value }) => handleRatingChange(value)}
                        onBlur={handleRatingBlur}
                        min="0"
                        max="5"
                        step="1"
                        inputWidth="100%"
                      />
                    </div>
                    <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validBusinessWeight}%</div>
                    <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                      {((safeParse(localTaskRating) * validBusinessWeight) / 100).toFixed(2)}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: '#0d47a1' }}>
                    {isManager && !isEmployee ? '👤 Employee\'s Self Rating' : 'Your Self Rating'}
                  </div>
                  <div className="apr-score-hint">
                    {isManager && !isEmployee 
                      ? 'Employee\'s evaluation of their business objective achievement' 
                      : 'Your evaluation of results against business targets'}
                  </div>
                </div>
                <div className="apr-score-cell-rating">
                  <div style={{
                    padding: '10px',
                    backgroundColor: appraisalData.employeeSelfRatingTask ? businessRatingScale[appraisalData.employeeSelfRatingTask]?.bgColor || '#f1f5f9' : '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: `1px solid ${appraisalData.employeeSelfRatingTask ? businessRatingScale[appraisalData.employeeSelfRatingTask]?.color || '#cbd5e1' : '#e2e8f0'}`,
                    fontWeight: 700,
                    color: appraisalData.employeeSelfRatingTask ? businessRatingScale[appraisalData.employeeSelfRatingTask]?.color : '#64748b'
                  }}>
                    {appraisalData.employeeSelfRatingTask ? `${appraisalData.employeeSelfRatingTask} - ${businessRatingScale[appraisalData.employeeSelfRatingTask]?.label}` : '—'}
                  </div>
                </div>
                <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validBusinessWeight}%</div>
                <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                  {((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight) / 100).toFixed(2)}
                </div>
              </div>
            )}

            {/* ✅ MANAGER'S RATING - ONLY shown when status is MANAGER_REVIEWED or FINALIZED */}
            {!isTopLevelUser && !isManager && 
              (appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.REVIEWED || 
               appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED) && (
              <div className="apr-score-row" style={{ 
                borderTop: '2px dashed #4f46e5', 
                marginTop: '4px',
                paddingTop: '12px',
                backgroundColor: '#f8faff'
              }}>
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: '#4f46e5', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    👔 Manager's Overall Business Rating
                  </div>
                  <div className="apr-score-hint">
                    Manager's evaluation of your business objective performance
                  </div>
                </div>
                <div className="apr-score-cell-rating">
                  <div style={{
                    padding: '10px',
                    backgroundColor: appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.bgColor || '#ede9fe' : '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: `2px solid ${appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.color || '#4f46e5' : '#e2e8f0'}`,
                    fontWeight: 700,
                    color: appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.color : '#64748b'
                  }}>
                   {appraisalData.managerRatingTask ? `${appraisalData.managerRatingTask} – ${businessRatingScale[appraisalData.managerRatingTask]?.label}` : (!isTopLevelUser ? 'Not rated yet' : '')}
                  </div>
                </div>
                <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validBusinessWeight}%</div>
                <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800, color: '#4f46e5' }}>
                  {((safeParse(appraisalData.managerRatingTask) * validBusinessWeight) / 100).toFixed(2)}
                </div>
              </div>
            )}

            {/* Manager's Rating when manager is viewing */}
            {isManager && !isEmployee && (
              <div className="apr-score-row" style={{ 
                borderTop: '2px dashed #e2e8f0', 
                marginTop: '4px',
                paddingTop: '12px'
              }}>
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: '#4f46e5' }}>
                    👔 Manager's Overall Business Rating
                  </div>
                  <div className="apr-score-hint">
                    Calculated from your per‑objective ratings ({recentObjectives.filter(o => o.managerRating).length} rated)
                  </div>
                </div>
                <div className="apr-score-cell-rating">
                  <div style={{
                    padding: '10px',
                    backgroundColor: appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.bgColor || '#f1f5f9' : '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: `1px solid ${appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.color || '#cbd5e1' : '#e2e8f0'}`,
                    fontWeight: 700,
                    color: appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.color : '#64748b'
                  }}>
                    {appraisalData.managerRatingTask ? `${appraisalData.managerRatingTask} – ${businessRatingScale[appraisalData.managerRatingTask]?.label}` : 'Not rated'}
                  </div>
                </div>
                <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validBusinessWeight}%</div>
                <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                  {((safeParse(appraisalData.managerRatingTask) * validBusinessWeight) / 100).toFixed(2)}
                </div>
              </div>
            )}

            {/* Manager's Business Comments - Show when available AND status is MANAGER_REVIEWED or FINALIZED */}
            {!isManager && 
              (appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED || 
               appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED) &&
              appraisalData.managerBusinessComments && (
                <div style={{ width: '100%', padding: '0.5rem 1.25rem 0.75rem 1.25rem' }}>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    color: 'var(--apr-text-secondary)', 
                    padding: '10px', 
                    background: '#f8fafc', 
                    borderRadius: '8px', 
                    border: '1px solid #e2e8f0',
                    borderLeft: `3px solid #4f46e5`
                  }}>
                    💬 <strong>Manager's Comments:</strong> {appraisalData.managerBusinessComments}
                  </div>
                </div>
              )}
          </div>
        </CriteriaSection>
      </div>

      {/* ── DIVIDER ── */}
      <hr className="apr-subsection-divider" />

      {/* Leadership & Behavioural Competencies */}
      <div className="apr-subsection apr-subsection--leadership">
        <div className="apr-subsection-header">
          <h5>👔 Leadership & Behavioural Competencies</h5>
          <span className="apr-subsection-weight">{validLeadershipWeight}% Weight</span>
        </div>
        
        <CriteriaSection 
        >
          <div>
            <div className="apr-score-header-row">
              <div className="apr-score-cell-main">Competency</div>
              <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Rating</div>
              <div className="apr-score-cell-weight">Weight</div>
              <div className="apr-score-cell-result">Score</div>
            </div>

            {leadershipLoading ? (
              <div className="apr-loading">
                <CircularLoader small />
                <span>Loading competencies...</span>
              </div>
            ) : (
              <>
                {leadershipCompetencies.map((comp) => {
                  const isManagerView = isManager && !isEmployee;
                  const currentRatings = isManagerView ? managerCompetencyRatings : competencyRatings;
                  const currentComments = isManagerView ? managerCompetencyComments : competencyComments;
                  const rating = currentRatings[comp.id] || '';
                  const comment = currentComments[comp.id] || '';
                  const score = rating ? ((parseFloat(rating) * (comp.weight / 100)) || 0).toFixed(2) : '0.00';
                  const canEdit = isManagerView ? canManagerEdit : canEmployeeEdit;
                  const isExpanded = expandedCompId === comp.id;

                  const shouldShowManagerFields = isManager || isTopLevelUser;

                  // In the competency card rendering:
                  const showManagerRating = 
                    (isTopLevelUser || (isManager && !isEmployee)) && 
                    appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && 
                    managerCompetencyRatings[comp.id];

                  // For the manager rating input - show it for CEO's boss
                  const canEditManager = 
                    (isManager && !isEmployee && canManagerEdit) || 
                    (isTopLevelUser && appraisalData.appraisalStatus !== APPRAISAL_STATUS_CODES.FINALIZED);
                                   
                  const managerRating = managerCompetencyRatings[comp.id] || '';

                  console.log(`🔍 Rendering competency ${comp.name}:`, {
                    id: comp.id,
                    rating: competencyRatings[comp.id],
                    allRatings: competencyRatings,
                    comment: competencyComments[comp.id],
                    isTopLevelUser: isTopLevelUser,
                    canEdit: canEmployeeEdit,
                    appraisalStatus: appraisalData.appraisalStatus
                  });

                  return (
                    <div key={comp.id} className="apr-competency-card">
                      {/* Row 1: Name | Weight | Score */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', width: '100%', alignItems: 'center', gap: '1rem' }}>
                        <div className="apr-score-cell-main">
                          <div className="apr-score-label">{comp.name}</div>
                          <div className="apr-score-hint">{comp.displayContent}</div>
                          {comp.behaviouralIndicators && Object.keys(comp.behaviouralIndicators).length > 0 && (
                            <button 
                              className="apr-indicator-btn"
                              onClick={() => setExpandedCompId(isExpanded ? null : comp.id)}
                              style={{ 
                                marginTop: '0.5rem',
                                background: isExpanded ? '#e8f5e9' : '#f5f5f5',
                                border: `1px solid ${isExpanded ? '#4caf50' : '#ddd'}`,
                                borderRadius: '20px',
                                padding: '4px 16px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                color: isExpanded ? '#2e7d32' : '#666',
                                transition: 'all 0.2s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {isExpanded ? '▲ Hide Indicators' : '▼ View Behavioural Indicators'}
                            </button>
                          )}
                        </div>
                        <div className="apr-score-cell-weight" style={{ textAlign: 'center', fontWeight: 600 }}>
                          {Math.round(comp.weight * 100)}%
                        </div>
                        <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                          {score}
                        </div>
                      </div>
                      
                      {/* Employee's Self Rating - shown to manager */}
                      {isManagerView && (
                        <div style={{ 
                          width: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '1rem', 
                          marginBottom: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--apr-text-secondary)' }}>
                            Employee’s Self Rating:
                          </span>
                          <div style={{
                            padding: '2px 12px',
                            backgroundColor: competencyRatings[comp.id] ? leadershipRatingScale[competencyRatings[comp.id]]?.bgColor || '#f1f5f9' : '#f1f5f9',
                            borderRadius: '4px',
                            border: `1px solid ${competencyRatings[comp.id] ? leadershipRatingScale[competencyRatings[comp.id]]?.color || '#cbd5e1' : '#e2e8f0'}`,
                            fontWeight: 600,
                            color: competencyRatings[comp.id] ? leadershipRatingScale[competencyRatings[comp.id]]?.color : '#64748b',
                            fontSize: '0.85rem'
                          }}>
                            {competencyRatings[comp.id] ? `${competencyRatings[comp.id]} – ${leadershipRatingScale[competencyRatings[comp.id]]?.label}` : 'Not rated'}
                          </div>
                          {competencyComments[comp.id] && (
                            <span style={{ fontSize: '0.8rem', color: 'var(--apr-text-secondary)', fontStyle: 'italic' }}>
                              “{competencyComments[comp.id]}”
                            </span>
                          )}
                        </div>
                      )}

                      {/* ✅ Manager's Rating - shown to employee ONLY when status is MANAGER_REVIEWED or FINALIZED */}
                      {showManagerRating && managerRating && (
                        <div style={{ 
                          width: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '1rem', 
                          marginBottom: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: '#f0f4ff',
                          borderRadius: '4px',
                          border: '1px solid #c7d2fe'
                        }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f46e5' }}>
                            👔 Manager's Rating:
                          </span>
                          <div style={{
                            padding: '2px 12px',
                            backgroundColor: leadershipRatingScale[managerRating]?.bgColor || '#ede9fe',
                            borderRadius: '4px',
                            border: `1px solid ${leadershipRatingScale[managerRating]?.color || '#4f46e5'}`,
                            fontWeight: 600,
                            color: leadershipRatingScale[managerRating]?.color || '#4f46e5',
                            fontSize: '0.85rem'
                          }}>
                            {managerRating} – {leadershipRatingScale[managerRating]?.label || 'Not rated'}
                          </div>
                          {managerCompetencyComments[comp.id] && (
                            <span style={{ fontSize: '0.8rem', color: 'var(--apr-text-secondary)', fontStyle: 'italic' }}>
                              “{managerCompetencyComments[comp.id]}”
                            </span>
                          )}
                        </div>
                      )}
                    
                      {/* Row 2: Rating Cards */}
                      <div style={{ width: '100%', marginTop: '0.25rem' }}>
                        {canEdit ? (
                          <RatingCardSelector
                            options={Object.entries(leadershipRatingScale || RATING_DEFINITIONS)
                              .sort(([a], [b]) => Number(b) - Number(a))
                              .map(([key, def]) => ({ value: key, label: def.label }))}
                            selected={rating}
                            onChange={(newRating) => {
                              if (isManagerView) {
                                setManagerCompetencyRatings(prev => ({ ...prev, [comp.id]: newRating }));
                                const allRatings = { ...managerCompetencyRatings, [comp.id]: newRating };
                                const validRatings = Object.values(allRatings).filter(r => r !== '');
                                const avg = validRatings.length > 0 ? (validRatings.reduce((sum, r) => sum + parseFloat(r), 0) / validRatings.length).toFixed(1) : '';
                                setAppraisalData(prev => ({ ...prev, managerRatingBehavioural: avg }));
                              } else {
                                setCompetencyRatings(prev => ({ ...prev, [comp.id]: newRating }));
                                const allRatings = { ...competencyRatings, [comp.id]: newRating };
                                const validRatings = Object.values(allRatings).filter(r => r !== '');
                                const avg = validRatings.length > 0 ? (validRatings.reduce((sum, r) => sum + parseFloat(r), 0) / validRatings.length).toFixed(1) : '';
                                setAppraisalData(prev => ({ ...prev, employeeSelfRatingBehavioural: avg }));
                              }
                              setTimeout(() => saveCompetencyRatings(), 300);
                            }}
                            disabled={!canEdit}
                            ratingScale={leadershipRatingScale}
                          />
                        ) : (
                          <div style={{
                            padding: '6px 12px',
                            backgroundColor: rating ? leadershipRatingScale[rating]?.bgColor || '#f1f5f9' : '#f8f9fa',
                            borderRadius: '8px',
                            border: `1px solid ${rating ? leadershipRatingScale[rating]?.color || '#cbd5e1' : '#e2e8f0'}`,
                            fontWeight: 700,
                            color: rating ? leadershipRatingScale[rating]?.color : '#64748b',
                            display: 'inline-block'
                          }}>
                            {rating ? `${rating} - ${leadershipRatingScale[rating]?.label}` : '—'}
                          </div>
                        )}
                      </div>

                      {/* Row 3: Comments */}
                      <div style={{ width: '100%', marginTop: '0.25rem' }}>
                        {canEdit ? (
                          <TextAreaField
                            label="Comments"
                            value={comment}
                            onChange={({ value }) => {
                                if (isManagerView) {
                                  setManagerCompetencyComments(prev => ({ ...prev, [comp.id]: value }));
                                } else {
                                  setCompetencyComments(prev => ({ ...prev, [comp.id]: value }));
                                }
                                // ADD THIS LINE TO SAVE COMMENTS
                                setTimeout(() => saveCompetencyRatings(), 300);
                              }}
                            
                            rows={2}
                            placeholder="Provide specific feedback..."
                            inputWidth="100%"
                          />
                        ) : (
                          comment && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--apr-text-secondary)', fontStyle: 'italic', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                              💬 <strong>{isManagerView ? 'Manager:' : 'Self:'}</strong> {comment}
                            </div>
                          )
                        )}
                      </div>

                      {/* Expanded Behavioural Indicators */}
                      {isExpanded && comp.behaviouralIndicators && (
                        <div className="apr-indicators-list" style={{ width: '100%', marginTop: '0.5rem' }}>
                          {[5, 4, 3, 2, 1].map(level => {
                            const indicator = comp.behaviouralIndicators[level];
                            if (!indicator) return null;
                            return (
                              <div key={level} className={`apr-indicator-row apr-indicator-row--${level}`}>
                                <div className="apr-indicator-level">{level}</div>
                                <div>{indicator}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* ============================================ */}
                      {/* ✅ PER-COMPETENCY MANAGER REVIEW PLACEHOLDER */}
                      {/* ============================================ */}
                      {isTopLevelUser && (
                        <div className="comp-manual-review" style={{ 
                          marginTop: '12px', 
                          paddingTop: '12px', 
                          borderTop: '2px dashed #0f5132',
                          backgroundColor: '#f5f8fa',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '4px'
                        }}>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'minmax(75px, 0.25fr) minmax(0, 1fr)',
                            gap: '12px'
                          }}>
                            <div>
                              <span style={{ fontSize: '0.65rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                Manager Rating (0-5):
                              </span>
                              <div style={{ 
                                borderBottom: '2px solid #0f5132', 
                                minHeight: '24px', 
                                width: '100%',
                                backgroundColor: '#fff',
                                borderRadius: '4px',
                                padding: '2px 8px'
                              }}>
                                <span style={{ color: '#999', fontSize: '0.75rem' }}>_____</span>
                              </div>
                            </div>
                            <div>
                              <span
                                style={{
                                  fontSize: '0.65rem',
                                  color: '#666',
                                  fontWeight: 600,
                                  display: 'block',
                                  marginBottom: '3px',
                                }}
                              >
                                Manager Comments: (Add Comments...)
                              </span>
                              <textarea
                                rows={6}
                                style={{
                                  width: '100%',
                                  minHeight: '90px',
                                  boxSizing: 'border-box',
                                  border: 'none',
                                  borderBottom: '2px solid #ccc',
                                  backgroundColor: '#fff',
                                  borderRadius: '4px',
                                  padding: '8px',
                                  fontSize: '0.7rem',
                                  color: '#333',
                                  resize: 'vertical',
                                  outline: 'none',
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}                

                {/* Overall Leadership Rating */}
                <div className="apr-score-total-row">
                  <div className="apr-score-cell-main">Overall Leadership Rating</div>
                  <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>
                    {isManager && !isEmployee ? appraisalData.managerRatingBehavioural : appraisalData.employeeSelfRatingBehavioural || '—'}
                  </div>
                  <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validLeadershipWeight}%</div>
                  <div className="apr-score-cell-result" style={{ textAlign: 'center' }}>
                    {((safeParse(isManager && !isEmployee ? appraisalData.managerRatingBehavioural : appraisalData.employeeSelfRatingBehavioural) * validLeadershipWeight) / 100).toFixed(2)}
                  </div>
                </div>

                {/* ✅ Manager's Overall Leadership Rating - shown ONLY when status is MANAGER_REVIEWED or FINALIZED */}
                {!isTopLevelUser && !isManager &&  
                  (appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED || 
                   appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED) &&
                  appraisalData.managerRatingBehavioural && (
                    <div className="apr-score-total-row" style={{ 
                      borderTop: '2px dashed #4f46e5',
                      backgroundColor: '#f8faff'
                    }}>
                      <div className="apr-score-cell-main" style={{ color: '#4f46e5' }}>
                        👔 Manager's Overall Leadership Rating
                      </div>
                      <div className="apr-score-cell-rating" style={{ textAlign: 'center', color: '#4f46e5' }}>
                        {appraisalData.managerRatingBehavioural} – {leadershipRatingScale[appraisalData.managerRatingBehavioural]?.label || ''}
                      </div>
                      <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validLeadershipWeight}%</div>
                      <div className="apr-score-cell-result" style={{ textAlign: 'center', color: '#4f46e5' }}>
                        {((safeParse(appraisalData.managerRatingBehavioural) * validLeadershipWeight) / 100).toFixed(2)}
                      </div>
                    </div>
                  )}

                {/* Manager's Leadership Comments - Show when available AND status is MANAGER_REVIEWED or FINALIZED */}
                {!isManager && 
                  (appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED || 
                   appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED) &&
                  appraisalData.managerComments && (
                    <div className="hide-in-print" style={{   
                    fontSize: '0.85rem', 
                    color: 'var(--apr-text-secondary)', 
                    padding: '10px', 
                    background: '#f8fafc', 
                    borderRadius: '8px', 
                    border: '1px solid #e2e8f0',
                    borderLeft: `3px solid #4f46e5`
                  }}>
                    💬 <strong>Manager's Comments:</strong> {appraisalData.managerComments}
                  </div>
                  )}
              </>
            )}
          </div>
        </CriteriaSection>

        {/* Total Performance Score Banner */}
        {!isTopLevelUser && (
        <div className="apr-total-banner hide-in-print"> 
          <div>
            <h4 style={{ margin: 0, color: '#1b5e20' }}>Total Performance Score</h4>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#388e3c' }}>
              Weighted result from Business ({validBusinessWeight}%) and Leadership ({validLeadershipWeight}%)
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="apr-total-value">
              {((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight + 
                safeParse(appraisalData.employeeSelfRatingBehavioural) * validLeadershipWeight) / 100).toFixed(2)}
            </div>
            <div style={{ fontWeight: 800, color: '#1b5e20', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {businessRatingScale[Math.round((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight + 
                safeParse(appraisalData.employeeSelfRatingBehavioural) * validLeadershipWeight) / 100)]?.label || 'In Progress'}
            </div>
          </div>
        </div>       
        )}

        {/* Comments Section */}
        <div className="apr-comments-section">
          {canEmployeeEdit ? (
            (() => {
                           
              return (
                <TextAreaField
                  label="Self-Assessment Comments"
                  helpText="Provide specific examples and context for your ratings"
                  value={localComments}
                  onChange={({ value }) => handleCommentsChange(value)}
                  onBlur={handleCommentsBlur}
                  rows={4}
                  inputWidth="100%"
                  disabled={!canEmployeeEdit}
                />
              );
            })()
          ) : (
            <div>
              <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>Self-Assessment Comments</label>
              <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', minHeight: '80px' }}>
                {appraisalData.employeeSelfComments || 'No comments provided'}
              </div>
            </div>
          )}
        </div>
      

        {/* Validation Banner - Show what's missing - Hidden in print mode */}
        {!isPrintMode && !readOnlyReport && isEmployee && appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.DRAFT && (
          (() => {
            const missingFields = getMissingFields();
            if (missingFields.length === 0) return null;
            
            return (
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff3cd', 
                borderRadius: '8px',
                border: '1px solid #ffc107',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#856404' }}>
                      Please complete the following before submitting:
                    </div>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', color: '#856404' }}>
                      {missingFields.map((field, index) => (
                        <li key={index}>{field}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* Actions Section with Status Messages */}
        {isEmployee && !readOnlyReport && (
          <div>
            {/* ✅ Show message when in submitted status (waiting for manager) */}
            {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED && (
              <div className="apr-actions-bar">
                <div style={{ padding: '8px 16px', backgroundColor: '#e3f2fd', borderRadius: '4px', width: '100%', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#1565c0' }}>
                    ⏳ Your appraisal has been submitted and is awaiting manager review. You cannot make changes at this time.
                  </span>
                </div>
              </div>
            )}

            {/* ✅ Show message when manager has reviewed (employee can respond) */}
            {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.REVIEWED && (
              <div className="apr-actions-bar">
                <div style={{ padding: '8px 16px', backgroundColor: '#fff8e1', borderRadius: '4px', width: '100%', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#856404' }}>
                    📋 Your manager has reviewed your appraisal. Please review the manager's ratings and provide your response below.
                  </span>
                </div>
              </div>
            )}

            {/* Actions when in DRAFT status */}
            {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.DRAFT && (
              <div className="apr-actions-bar">
                <button
                  className="apr-btn apr-btn--secondary"
                  style={{ width: '120px' }}
                  onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.DRAFT)}>
                  Save Draft
                </button>
                <button
                  className="apr-btn apr-btn--primary"
                  style={{ minWidth: isTopLevelUser ? '220px' : '180px' }}
                  onClick={handleSubmit}
                  disabled={
                    !appraisalData.employeeSelfComments || 
                    !appraisalData.employeeSelfComments.trim() ||
                    leadershipCompetencies.some(comp => !competencyRatings[comp.id]) ||
                    !appraisalData.employeeSelfRatingTask
                  }
                  title={
                    !appraisalData.employeeSelfComments || !appraisalData.employeeSelfComments.trim()
                      ? 'Please provide Self-Assessment Comments'
                      : leadershipCompetencies.some(comp => !competencyRatings[comp.id])
                      ? 'Please rate all Leadership Competencies'
                      : !appraisalData.employeeSelfRatingTask
                      ? 'Please complete Business Objectives ratings'
                      : ''
                  }
                >
                  {isTopLevelUser ? 'Submit & Auto-Approve' : 'Submit for Review'}
                </button>
              </div>
            )}

            {/* ─── Manager Manual Review Placeholder ─── */}
            {isTopLevelUser && (
              <div style={{ 
                marginTop: '16px', 
                paddingTop: '16px', 
                borderTop: '2px dashed #ccc',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h6 style={{ 
                  margin: '0 0 12px 0', 
                  color: '#4f46e5', 
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📝</span> Manager's Review 
                </h6>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '16px',
                  marginBottom: '12px'
                }}>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                      Manager Rating (0 – 5):
                    </span>
                    <div style={{ 
                      borderBottom: '2px solid #999', 
                      minHeight: '30px', 
                      width: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '4px 8px'
                    }}>
                      <span style={{ color: '#999', fontSize: '0.85rem' }}>____</span>
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                      Status:
                    </span>
                    <div style={{ 
                      borderBottom: '2px solid #999', 
                      minHeight: '30px', 
                      width: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '4px 8px'
                    }}>
                      <span style={{ color: '#999', fontSize: '0.85rem' }}>Approved / Rejected / Pending</span>
                    </div>
                  </div>
                </div>
                <div>
                <span
                  style={{
                    fontSize: '1rem', // Increased from 0.75rem
                    color: '#666',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  Manager Comments: (Write your feedback here...)
                </span>

                <div
                  style={{
                    borderBottom: '2px solid #ccc',
                    minHeight: '50px',
                    width: '100%',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <textarea
                    rows={6}
                    style={{
                      width: '100%',
                      minHeight: '90px',
                      boxSizing: 'border-box',
                      border: 'none',
                      borderBottom: '2px solid #ccc',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '8px',
                      fontSize: '0.7rem',
                      color: '#333',
                      resize: 'vertical',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>
              </div>
            )}

            {/* ✅ Show finalized message */}
            {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && (
              <div className="apr-actions-bar">
                <div style={{ padding: '8px 16px', backgroundColor: '#d4edda', borderRadius: '4px', width: '100%', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#155724' }}>
                    ✅ This appraisal has been finalized and locked. No further changes can be made.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Employee Finalization - Show when manager has reviewed and employee can respond */}
        {isEmployee && !readOnlyReport &&
          appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.REVIEWED &&
          !isFinalizedAndLocked && (
            <EmployeeResponseSection
              onFinalize={handleFinalizeAfterReview}
              disabled={loading}
              managerRatingTask={appraisalData.managerRatingTask}
              managerRatingBehavioural={appraisalData.managerRatingBehavioural}
              managerComments={appraisalData.managerComments}
              initialRemarks={employeeResponseRemarks}
            />
          )}
      </div>
    </div>
  </div>
)}

      {/* Employee Response Summary - Show after finalization */}
      {!isTopLevelUser && appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && employeeResponseStatus && (
        <EmployeeResponseSummary
        className="hide-in-print"  // ✅ ADD THIS
        responseStatus={employeeResponseStatus}
        responseRemarks={employeeResponseRemarks}
        acceptedTaskRating={employeeAcceptedRatingTask}
        acceptedBehaviouralRating={employeeAcceptedRatingBehavioural}
        managerRatingTask={appraisalData.managerRatingTask}
        managerRatingBehavioural={appraisalData.managerRatingBehavioural}
      />
      )}

      {/* ── No Appraisal Yet (Manager View) ─────────────────── */}
      {isManager && !appraisalData.eventId && (
        <div style={{ marginBottom: '1rem' }}>
          <NoticeBox warning title="No appraisal submitted yet">
            This employee has not submitted an appraisal for the {isMidYear ? 'Mid-Year' : 'Year-End'} review period.
            Please ask them to complete and submit their self-appraisal first.
            <div style={{ marginTop: '0.75rem' }}>
              <Button small secondary onClick={() => window.location.reload()}>
                Refresh
              </Button>
            </div>
          </NoticeBox>
        </div>
      )}

      {/* ── Manager / Evaluator Appraisal ───────────────────── */}
      {isManager && appraisalData.eventId && (
        <div className="apr-section">
          <div className="apr-card">
            <div className="apr-card-header">
            <div className="apr-card-header-left">
              <h4 className="apr-card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                Objectives List     
              </h4>
            </div>
          </div>

            <div className="apr-card-body" style={{ padding: '1.25rem 1.5rem' }}>
              {/* Table Header */}
              <div className="apr-score-header-row">
                <div className="apr-score-cell-main">Evaluation Criteria</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Recommended Rating</div>
                <div className="apr-score-cell-weight">Weight</div>
                <div className="apr-score-cell-result">Score</div>
              </div>

              {/* Business Objectives */}
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: 'var(--apr-primary)' }}>Business Objectives</div>
                  <div className="apr-score-hint">Performance against SMART objectives and KPIs</div>
                </div>
                <div className="apr-score-cell-rating">
                  <InputField
                    disabled={!canManagerEdit}
                    type="number"
                    value={String(appraisalData.managerRatingTask ?? '')}
                    onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingTask: value }))}
                    min="0"
                    max="5"
                    step="1"
                    inputWidth="100%"
                    placeholder="0-5"
                  />
                </div>
                <div className="apr-score-cell-weight" style={{ fontWeight: 600 }}>50%</div>
                <div className="apr-score-cell-result" style={{ fontWeight: 800 }}>
                  {calculateTotalScore(appraisalData.managerRatingTask, '')}
                </div>
              </div>

              {/* Behavioural */}
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: 'var(--apr-primary)' }}>Leadership & Values</div>
                  <div className="apr-score-hint">Evaluation of core competencies and professional behavior</div>
                </div>
                <div className="apr-score-cell-rating">
                  <InputField
                    disabled={!canManagerEdit}
                    type="number"
                    value={String(appraisalData.managerRatingBehavioural ?? '')}
                    onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingBehavioural: value }))}
                    min="0"
                    max="5"
                    step="1"
                    inputWidth="100%"
                    placeholder="0-5"
                  />
                </div>
                <div className="apr-score-cell-weight" style={{ fontWeight: 600 }}>50%</div>
                <div className="apr-score-cell-result" style={{ fontWeight: 800 }}>
                  {calculateTotalScore('', appraisalData.managerRatingBehavioural)}
                </div>
              </div>

              {/* Total Score for Manager */}
              <div className="apr-score-total-row" style={{ background: 'var(--apr-primary-light)', border: 'none' }}>
                <div className="apr-score-cell-main" style={{ color: 'var(--apr-primary-dark)' }}>Weighted Total Evaluation Score</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center', fontSize: '1.25rem', color: 'var(--apr-primary-dark)' }}>
                  {computed.managerTotal}
                </div>
                <div className="apr-score-cell-weight">100%</div>
                <div className="apr-score-cell-result" style={{ fontSize: '1.25rem', color: 'var(--apr-primary-dark)' }}>
                  {calculateTotalScore(appraisalData.managerRatingTask, appraisalData.managerRatingBehavioural)}
                </div>
              </div>

              {/* Manager Comments */}
              <div style={{ marginTop: '2rem' }}>
                <TextAreaField
                  label="Manager's Narrative Feedback"
                  value={appraisalData.managerComments}
                  rows={4}
                  inputWidth="100%"
                  disabled={!canManagerEdit}
                  onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerComments: value }))}
                  placeholder="Provide detailed feedback on performance and development areas..."
                />
              </div>

              {/* Manager Actions */}
              {isManager && !readOnlyReport && (
                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                  {(appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED ||
                    appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED) &&
                    !employeeResponseStatus && (
                      <button
                        className="apr-btn apr-btn--primary"
                        style={{ minWidth: '240px' }}
                        onClick={handleManagerReview}
                        disabled={!appraisalData.managerRatingTask || !appraisalData.managerRatingBehavioural}
                      >
                        {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED
                          ? 'Submit Final Manager Review'
                          : 'Update Manager Review'}
                      </button>
                    )}
                  {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED &&
                    employeeResponseStatus && (
                      <div style={{ width: '100%' }}>
                        <NoticeBox info title="Review Submitted">
                          Your evaluation has been submitted and the employee has already responded. You can no longer edit.
                        </NoticeBox>
                      </div>
                    )}
                  {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && (
                    <div style={{ width: '100%' }}>
                      <NoticeBox info title="Finalized">
                        This appraisal is finalized and locked.
                      </NoticeBox>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Employee Response Section - Shown when manager has reviewed */}
      {isEmployee && !readOnlyReport &&
        appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED &&
        !isFinalizedAndLocked && (
          <EmployeeResponseSection
            onFinalize={handleFinalizeAfterReview}
            disabled={loading}
            managerRatingTask={appraisalData.managerRatingTask}
            managerRatingBehavioural={appraisalData.managerRatingBehavioural}
            managerComments={appraisalData.managerComments}
            initialRemarks={employeeResponseRemarks}
          />
        )}

      {/* Employee Finalization - now combined into the EmployeeResponseSection */}

      {/* ── Status Footer ────────────────────────────────────── */}
      {!isTopLevelUser && (
          <div className="apr-status-footer hide-in-print">
          <span className="apr-status-footer-label">Current Status: </span>
          <StatusChip status={appraisalData.appraisalStatus} />
          {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && (
            <span style={{ color: 'var(--apr-success)', display: 'flex', alignItems: 'center' }}>
              <Lock size={14} style={{ marginRight: 4 }} /> Locked
            </span>
          )}
        </div>
      )}

      {isTaskModalOpen && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          engine={engine}
        />
      )}
    </div>
  );
};

export default AppraisalForm;